import { createSelector } from '@reduxjs/toolkit'

import { formatClock, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import { sameId } from '../../../shared/api/wire'
import type { CommandFeedback } from '../../../shared/model/feedback'
import { formatKopecks, formatPrice } from '../../../shared/model/format'
import { formatMoment, makeQueryViewSelector, readyData } from '../../../shared/model/query'
import type { AqsiGood, AqsiGoodsPage } from '../../catalog/model/types'
import { absentReason, type CommonGoodHit, type CommonGoodsPage, type DeliveryOffer } from './commonSearch'
import type { AqsiRegisters, AqsiSession } from '../../rmk/model/types'
import { makeSelectSourceCaption } from '../../source/model/selectors'
import type { AqsiSource } from '../../source/model/types'
import { canEditCart } from './cartRules'
import { CART_SAVED_TEXT, CART_SAVING_TEXT, cartRetryText, cartSavingAttemptText, commandAttemptText, commandRetryText, lookupRetryText, SEARCH_RETRY_TEXT } from './healthTexts'
import { PICKER_PAGE_SIZE, type SaleNoticeVm } from '@krp/sale-ui'
import { lineAmountKopecks, priceError, priceInputText, priceKopecks } from '@krp/sale-ui'
import { normalizeQuantity, quantityError } from '@krp/sale-ui'
import { blocksSaleCommands } from './saleCommandsSlice'
import { PREPARE_COMMANDS, WAIT_RECEIPT_COMMAND, type PaymentAttempt } from './paymentAttemptsSlice'
import { hasLateSlip, LATE_PAYMENT_SLIP, paymentStageOf, purchaseOf, SERVICE_RECEIPT_GRACE_MS } from './paymentFlow'
import { receiptsAheadOf } from './fastPayment'
import { FAST_HAND_OFF_DONE_TEXT, PAY_WAITING_RECEIPT_TEXT, RECEIPT_AHEAD_STATUS_TEXT } from './fastTexts'
import { COLLAPSIBLE_PAYMENT_STAGES, paymentFlowOfProgress, type PaymentFlowVm, type PaymentNoticeVm, type PaymentResolveVm, type PaymentStage, type RecoveryPanelVm } from '@krp/sale-ui'
import { isRetryPayment, needsAqsiCheck, recoveryPanelVmOf, sendableAction } from './aqsiStatePanel'
import type { AqsiSaleState } from './aqsiStateTypes'
import { receiptNumberRequiredBy, receiptVerdictOf } from './receiptVerdict'
import { receiptWatchNote, waitSpanText } from './receiptWatch'
import { EMPTY_RESOLVE_DRAFT, lineKey } from './saleSlice'
import { saleOf, seatRefundOf } from './seatActivity'
import type {
  AqsiHeldCart,
  AqsiHeldCarts,
  AqsiSale,
  AqsiSaleAttention,
  AqsiSaleLine,
  AqsiSaleCheck,
  AqsiSaleCheckReason,
  AqsiSaleOperation,
  AqsiSeatActivity,
  CartDraft,
  CartLineVm,
  CartSyncVm,
  FeedbackHintVm,
  FiscalState,
  HeldCartVm,
  HeldVm,
  LatePaymentVm,
  OutsideReceiptVm,
  PayButtonVm,
  PaymentKind,
  PaymentState,
  PendingLookup,
  PickerRowVm,
  ParkVm,
  ParkedNoticeVm,
  PickerVm,
  ProgressStepVm,
  ProgressVm,
  ReceiptRetryChecked,
  ReceiptRetryDraft,
  ReceiptRetryVm,
  ResolveDraft,
  ResolveVm,
  SaleCheckReasonVm,
  SaleCheckVm,
  SaleCommand,
  SaleCommandVm,
  SaleOperationVm,
  SaleSeatIntent,
  AqsiSaleVm,
  SearchVm,
  StateBadgeVm,
  Tone,
} from './types'

/**
 * Вход vm окна продажи — НЕ RootState: binding отдаёт результаты запросов
 * одного rid и намерение места.
 */
export interface SaleVmInput {
  resourceId: string
  /** Кабинеты для кассы (`SOURCE_ARGS.rmk`) — подпись источника. */
  sources: QueryResultLike<readonly AqsiSource[]>
  /**
   * Кабинеты, где вам разрешено продавать (`SOURCE_ARGS.sales`). Не передано
   * или ещё не прочитано — продажу не запрещать: право проверит служба.
   */
  sellers?: QueryResultLike<readonly AqsiSource[]>
  /**
   * Кабинеты, где вам разрешено ставить свою цену позиции (`SOURCE_ARGS.price`, RP-FB8).
   * Не передано — не запрещать (право проверит служба); передано и не прочитано — поля цены нет:
   * отказ службы на цену отверг бы всю корзину.
   */
  priceEditors?: QueryResultLike<readonly AqsiSource[]>
  /**
   * Моя работа за кассой — из нашей базы (`sessions/mine`), кабинет не нужен: идущая продажа видна, даже когда
   * кабинет не ответил на список касс.
   */
  mySession: QueryResultLike<AqsiSession | null>
  /** Кассы кабинета — только имя кассы в подписи; не прочитаны — подпись без имени. */
  registers?: QueryResultLike<AqsiRegisters> | undefined
  /** `GET sales/active`: продажа, возврат на кассе (C4b) или `null`. */
  activeSale: QueryResultLike<AqsiSeatActivity> | undefined
  operation: QueryResultLike<AqsiSaleOperation> | undefined
  finishedSale: QueryResultLike<AqsiSale> | undefined
  goods: QueryResultLike<AqsiGoodsPage> | undefined
  seat: SaleSeatIntent
  /** Очередь корзины кассы: несохранённая правка (`cartDraftOf`); `undefined` — всё сохранено. */
  draft?: CartDraft | undefined
  command: SaleCommand | undefined
  feedback: CommandFeedback | undefined
  // ——— UI-RMK ———
  /** Выдача строки поиска по тексту, который уже ушёл на сервер (после паузы ввода). */
  searchGoods?: QueryResultLike<CommonGoodsPage> | undefined
  /** Текст, по которому запрошена `searchGoods`: отличается от набранного — выдача ещё не пришла. */
  searchedText?: string
  heldCarts?: QueryResultLike<AqsiHeldCarts> | undefined
  /** Проверка корзины службой (С2) для сохранённой версии продажи. */
  check?: QueryResultLike<AqsiSaleCheck> | undefined
  /** Малая страница снимка каталога — время снимка («Цены кассы на 14:32»). */
  snapshot?: QueryResultLike<AqsiGoodsPage> | undefined
  /**
   * Момент «сейчас» (мс) для причин зависания чека и срока повтора. Binding отдаёт тикающее значение (`useClockBucket`); не передано —
   * текущее время, округлённое до `CLOCK_BUCKET_MS`: результат селектора стабилен внутри окна, а не меняется на каждый вызов.
   */
  now?: number
  /** Число отложенных продаж кассы (`aqsiParkedCount`); не передано — заметки нет. */
  parkedCount?: QueryResultLike<number> | undefined
  backgroundSales?: QueryResultLike<readonly AqsiSale[]> | undefined
  /** Попытка оплаты кассы (RP-PAY-ONE): нажатия кассира; не передано — попытки нет. */
  attempt?: PaymentAttempt | undefined
  /** Ход и отказы команд цикла оплаты этой попытки (не общий итог команд кассы). */
  attemptFeedback?: CommandFeedback | undefined
  /** Черновик ручной сверки текущей операции — форма в окне оплаты. */
  resolveDraft?: ResolveDraft | undefined
  /**
   * «Проверка в AQSI» текущей продажи (`aqsiSaleState`, RP-CASH-RECOVERY §4.1) — когда она нужна (`needsAqsiCheck`); не передано —
   * панель показывает «Проверяем в AQSI…» до ответа.
   */
  aqsiState?: QueryResultLike<AqsiSaleState> | undefined
  /**
   * Быстрая оплата (RP-SALE-FAST, шаг 9): включена ли она у кассы (`enabled`) и продажа, которая только что ушла в фон (`flashSaleId`:
   * окно оплаты показывает галочку «Оплата прошла» и закрывается само). Не передано — режим выключен, всё как прежде.
   */
  fast?: { enabled: boolean; flashSaleId: string | null } | undefined
}

/** Касса моей работы за кассой — для подписки binding на продажу кассы. */
export function mineSeatOf(session: AqsiSession | null | undefined): { registerKey: string; deviceId: number } | null {
  return session == null ? null : { registerKey: session.registerKey, deviceId: session.deviceId }
}

/**
 * Операция, ход которой показывает окно: идущее/неизвестное списание, затем
 * идущий/неизвестный чек; доказанный отказ — тоже (его текст важен человеку).
 */
export function currentOperationOf(activity: AqsiSeatActivity | undefined): string | null {
  // Операции возврата окно продажи не ведёт: их ход и сверка — в окне возврата.
  const sale = saleOf(activity)
  if (sale === null) return null
  // Служба назвала текущую операцию в ответе продажи (RP-PAY-ONE, шаг 6) — она и есть ход окна.
  if (sale.currentOperation != null) return sale.currentOperation.operationId
  if (sale.purchaseOperationId !== null && ['InProgress', 'Unknown', 'Failed'].includes(sale.payment)) {
    return sale.purchaseOperationId
  }
  if (sale.receiptOperationId !== null && ['InProgress', 'Unknown', 'Failed'].includes(sale.fiscal)) {
    return sale.receiptOperationId
  }
  return null
}

/**
 * Продажа кассы, которой нужна «Проверка в AQSI» (RP-CASH-RECOVERY §4.1): binding подписывается на `aqsiSaleState` только для неё;
 * `null` — панели нет.
 */
export function aqsiCheckSaleOf(activity: AqsiSeatActivity | undefined, read: AqsiSaleOperation | undefined): string | null {
  const sale = saleOf(activity)
  return sale !== null && needsAqsiCheck(sale, operationOfSale(sale, read)) ? sale.id : null
}

/**
 * Операция, по которой окно решает этап: из ОТВЕТА ПРОДАЖИ (`currentOperation`, служба 0.89.0) — та же версия, что и продажа, поэтому
 * промежуточного «терминал отказал, а продажа ещё идёт» нет; отдельное чтение операции дополняет её подробностями (слип, проверки, версия
 * для сверки) и остаётся запасным путём у прежней службы.
 */
export function operationOfSale(sale: AqsiSale | null, read: AqsiSaleOperation | undefined): AqsiSaleOperation | undefined {
  if (sale === null) return undefined
  const matching = read !== undefined && sameId(read.saleId, sale.id) ? read : undefined
  const current = sale.currentOperation
  if (current == null) return matching
  const base = matching !== undefined && sameId(matching.operationId, current.operationId) ? matching : undefined
  return {
    operationId: current.operationId,
    saleId: sale.id,
    registerKey: base?.registerKey ?? '',
    deviceId: base?.deviceId ?? null,
    amountKopecks: base?.amountKopecks ?? sale.totalKopecks,
    attemptState: base?.attemptState ?? '',
    externalOperationId: base?.externalOperationId ?? (current.accepted ? current.operationId : null),
    resultId: base?.resultId ?? null,
    note: base?.note ?? null,
    createdAt: base?.createdAt ?? current.sentAt ?? sale.updatedAt,
    checkedAt: base?.checkedAt ?? null,
    resolvedAt: base?.resolvedAt ?? null,
    nextCheckAt: base?.nextCheckAt ?? null,
    // Версию для сверки знает только отдельное чтение: без него — «не прочитана» (`-1`), сверка ждёт чтения.
    version: base?.version ?? -1,
    resolution: base?.resolution ?? null,
    kind: current.kind,
    state: current.state,
    providerStatus: current.providerStatus,
    providerMessage: current.providerMessage,
    nextAction: current.nextAction,
    sentAt: current.sentAt ?? base?.sentAt ?? null,
  }
}

export const MONEY: Readonly<Record<PaymentState, Omit<StateBadgeVm, 'label'>>> = {
  NotRequired: { text: 'Наличные — терминал не нужен', tone: 'neutral', icon: 'idle' },
  Pending: { text: 'Не оплачено', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'Списание идёт на терминале', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'Оплачено', tone: 'success', icon: 'ok' },
  Failed: { text: 'Списание не прошло — денег не взяли', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'Исход списания неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
}

export const RECEIPT: Readonly<Record<FiscalState, Omit<StateBadgeVm, 'label'>>> = {
  Pending: { text: 'Чек не пробит', tone: 'neutral', icon: 'idle' },
  InProgress: { text: 'Чек пробивается', tone: 'neutral', icon: 'progress' },
  Succeeded: { text: 'Чек пробит', tone: 'success', icon: 'ok' },
  Failed: { text: 'Чек не пробит — касса отказала', tone: 'danger', icon: 'failed' },
  Unknown: { text: 'Исход чека неизвестен — повтор запрещён', tone: 'warning', icon: 'unknown' },
  IssuedOutside: { text: 'Чек пробит на кассе вне программы', tone: 'success', icon: 'ok' },
}

const OPERATION_STATE: Readonly<Record<AqsiSaleOperation['state'], { text: string; tone: SaleOperationVm['tone'] }>> = {
  Pending: { text: 'записана службой, отправляется в кабинет', tone: 'neutral' },
  Sent: { text: 'отправлена — касса выполняет', tone: 'neutral' },
  Succeeded: { text: 'выполнена', tone: 'success' },
  Failed: { text: 'не выполнена', tone: 'danger' },
  Unknown: { text: 'исход неизвестен', tone: 'warning' },
}

const KIND_TEXT: Readonly<Record<AqsiSaleOperation['kind'], string>> = {
  purchase: 'Списание с карты',
  receipt: 'Чек',
  refundPayment: 'Возврат на карту',
  refundReceipt: 'Чек возврата',
}

/** Итог денежной операции — слип терминала: у списания и у возврата на карту. */
export function isSlipOperation(kind: AqsiSaleOperation['kind']): boolean {
  return kind === 'purchase' || kind === 'refundPayment'
}

function operationDetails(operation: AqsiSaleOperation): string[] {
  const resolution = operation.resolution
  return [
    operation.providerStatus !== null ? `Кабинет: ${operation.providerStatus}` : null,
    operation.providerMessage,
    operation.note,
    operation.resultId !== null ? `${isSlipOperation(operation.kind) ? 'Слип' : 'Чек'}: ${operation.resultId}` : null,
    operation.sentAt !== null ? `Отправлена: ${formatMoment(operation.sentAt)}` : null,
    operation.checkedAt !== null ? `Проверена: ${formatMoment(operation.checkedAt)}` : null,
    resolution !== null
      ? `Сверено вручную: ${resolution.resolvedBy}, ${formatMoment(resolution.at)} — ${resolution.reason}`
      : null,
  ].filter((line): line is string => line !== null && line !== '')
}

function operationVm(
  operationId: string | null,
  view: QueryView | null,
  status: AqsiSaleOperation | undefined,
  fromSale = false,
): SaleOperationVm | null {
  if (operationId === null) return null
  // Операция из ответа продажи (`currentOperation`) не ждёт отдельного чтения; иначе — только прочитанная.
  const current = fromSale ? status : view !== null ? readyData(view, status) : undefined
  if (current === undefined || !sameId(current.operationId, operationId)) {
    return {
      operationId,
      kindText: 'Операция',
      stateText: view?.status === 'loading' || view?.status === 'idle' ? 'статус читается…' : 'статус не прочитан',
      tone: 'warning',
      details: view?.error != null ? [view.error.message] : [],
      version: null,
      canReconcile: false,
      manualRequired: false,
      polling: false,
      accepted: false,
    }
  }
  const state = OPERATION_STATE[current.state]
  const unresolved = current.state !== 'Succeeded' && current.state !== 'Failed'
  return {
    operationId,
    kindText: KIND_TEXT[current.kind],
    stateText: state.text,
    tone: state.tone,
    details: operationDetails(current),
    version: current.version >= 0 ? current.version : null,
    canReconcile: unresolved && current.nextAction === 'Reconcile',
    manualRequired: unresolved && current.nextAction === 'ManualReconciliation',
    polling: unresolved && current.nextAction === 'Reconcile',
    accepted: unresolved && current.externalOperationId !== null,
  }
}

const COMMAND_KIND_TEXT: Readonly<Record<SaleCommand['kind'], string>> = {
  pay: 'Оплата',
  fiscalize: 'Чек',
  cancel: 'Отмена продажи',
  outside: 'Привязать уже пробитый чек',
}

/** Слова отправки команды: первая попытка — `first`; пауза перед повтором и повторная попытка называют номер (RP-SALE-FAST, шаг 6). */
function commandSendingText(command: SaleCommand, first: string): string {
  if (command.retryAt !== null) return commandRetryText(command)
  return command.attempt > 1 ? commandAttemptText(command) : first
}

function commandVm(command: SaleCommand | undefined): SaleCommandVm | null {
  if (command === undefined) return null
  const kindText = COMMAND_KIND_TEXT[command.kind]
  switch (command.phase) {
    case 'sending':
      return { kindText, phase: command.phase, text: commandSendingText(command, 'Команда отправлена, ждём ответа службы…') }
    case 'unknown':
      return {
        kindText,
        phase: command.phase,
        text: 'Ответ не получен: неизвестно, дошла ли команда. Повтор запрещён — окно само проверяет состояние на кассе.',
      }
    case 'not-arrived':
      return {
        kindText,
        phase: command.phase,
        text: 'Служба так и не узнала эту команду — продажа не изменилась. Можно отдать команду заново: это новая операция.',
      }
  }
}

function goodOf(goods: AqsiGoodsPage | undefined, productKey: string): AqsiGood | undefined {
  return goods?.items.find((good) => good.goodId === productKey)
}

function cartLines(
  sale: AqsiSale | null,
  seat: SaleSeatIntent,
  draft: CartDraft | undefined,
  goods: AqsiGoodsPage | undefined,
  lineReasons: ReadonlyMap<string, readonly SaleCheckReasonVm[]>,
): CartLineVm[] {
  const serverLines = sale?.lines ?? []
  const byKey = new Map(serverLines.map((line) => [lineKey({ productKey: line.productKey, barcode: line.barcode }), line]))
  const rows: Omit<CartLineVm, 'selected' | 'blocked'>[] =
    draft === undefined
      ? serverLines.map((line) => {
          const productKey = line.productKey
          const good = goodOf(goods, productKey)
          return {
            key: lineKey({ productKey, barcode: line.barcode }),
            productKey,
            barcode: line.barcode,
            name: line.name,
            quantity: line.quantity,
            // Единица и «весовой» — из позиции службы (0.22.0); прежняя служба их не отдаёт — из снимка каталога.
            unit: line.unit ?? good?.unit ?? null,
            isWeight: (line.isWeight ?? good?.isWeight) === true,
            priceText: formatKopecks(line.priceKopecks),
            amountText: formatKopecks(line.amountKopecks),
            amountKopecks: line.amountKopecks,
            quantityError: null,
            ...savedPrice(line),
          }
        })
      : draft.lines.map((line) => {
          const key = lineKey(line)
          const server = byKey.get(key)
          const good = goodOf(goods, line.productKey)
          const isWeight = line.isWeight ?? server?.isWeight ?? good?.isWeight ?? null
          const price = line.price ?? null
          const badPrice = price === null ? null : priceError(price)
          const typedKopecks = price !== null && badPrice === null ? priceKopecks(price) : null
          // Цена строки сейчас: набранная своя, а снятая — цена кабинета, которую служба уже назвала.
          const shownKopecks =
            typedKopecks ?? (price === null && server !== undefined ? (server.catalogPriceKopecks ?? server.priceKopecks) : null)
          // Решает служба: пока цена и количество строки те, что она приняла, сумма — её. Правку, которую она ещё
          // не видела, считаем сразу по её формуле и не заставляем ждать паузу и ответ (RP-FB15).
          const samePrice =
            server !== undefined &&
            (price === null ? server.catalogPriceKopecks == null : typedKopecks === server.priceKopecks)
          const sameQuantity =
            samePrice && server !== undefined && normalizeQuantity(server.quantity) === normalizeQuantity(line.quantity)
          const problem = quantityError(line.quantity, isWeight)
          const amountKopecks = sameQuantity
            ? server.amountKopecks
            : shownKopecks !== null && problem === null
              ? lineAmountKopecks(shownKopecks, line.quantity)
              : null
          return {
            key,
            productKey: line.productKey,
            barcode: line.barcode,
            name: line.name !== '' ? line.name : (server?.name ?? good?.name ?? line.productKey),
            quantity: line.quantity,
            unit: line.unit ?? server?.unit ?? good?.unit ?? null,
            isWeight: isWeight === true,
            // Своя цена видна сразу; снятая — цена кабинета, которую служба назвала.
            priceText: shownKopecks !== null ? formatKopecks(shownKopecks) : null,
            amountText: amountKopecks !== null ? formatKopecks(amountKopecks) : null,
            amountKopecks,
            quantityError: problem,
            price,
            catalogPrice: server === undefined ? null : priceInputText(server.catalogPriceKopecks ?? server.priceKopecks),
            priceError: badPrice,
            catalogPriceText:
              price !== null && server?.catalogPriceKopecks != null ? `В кабинете ${formatKopecks(server.catalogPriceKopecks)}` : null,
            priceChangedBy: price !== null ? (server?.priceChangedBy ?? null) : null,
          }
        })
  // Полоса правки: выбранная позиция, а без выбора (или после удаления выбранной) — первая.
  const selected = rows.some((row) => row.key === seat.selectedLineKey) ? seat.selectedLineKey : (rows[0]?.key ?? null)
  return rows.map((row) => ({ ...row, selected: row.key === selected, blocked: lineReasons.get(row.key) ?? NO_REASONS }))
}

/** Итог несохранённой правки: сумма строк, если посчитаны все; хоть одна ждёт службу — `null` (итог остаётся службы). */
function draftTotalKopecks(isDraft: boolean, lines: readonly CartLineVm[]): number | null {
  if (!isDraft) return null
  let total = 0
  for (const line of lines) {
    if (line.amountKopecks === null) return null
    total += line.amountKopecks
  }
  return total
}

/** Цена кассира сохранённой позиции: служба называет цену кабинета и кто поменял. */
function savedPrice(line: AqsiSaleLine) {
  const catalog = line.catalogPriceKopecks ?? null
  return {
    price: catalog === null ? null : priceInputText(line.priceKopecks),
    catalogPrice: priceInputText(catalog ?? line.priceKopecks),
    priceError: null,
    catalogPriceText: catalog === null ? null : `В кабинете ${formatKopecks(catalog)}`,
    priceChangedBy: catalog === null ? null : (line.priceChangedBy ?? null),
  }
}

const NO_REASONS: readonly SaleCheckReasonVm[] = []
const NO_LINE_REASONS: ReadonlyMap<string, readonly SaleCheckReasonVm[]> = new Map()

function reasonVm(reason: AqsiSaleCheckReason): SaleCheckReasonVm {
  return {
    code: reason.code,
    text: reason.message,
    detail: reason.detail,
    fixBy: reason.fixBy === 'administrator' ? 'administrator' : 'cashier',
    removable: reason.action === 'remove-line',
  }
}

/** Проверка относится к ТОЙ версии продажи, что на экране; устаревшая (корзину уже правили) не показывается. */
function currentCheck(check: AqsiSaleCheck | undefined, sale: AqsiSale | null): AqsiSaleCheck | undefined {
  return check !== undefined && sale !== null && sameId(check.saleId, sale.id) && check.saleVersion === sale.version ? check : undefined
}

function isLineReason(reason: AqsiSaleCheckReason): boolean {
  return reason.scope === 'line' && (reason.goodId !== null || reason.ordinal !== null)
}

function lineReasonsOf(check: AqsiSaleCheck | undefined, sale: AqsiSale | null): ReadonlyMap<string, readonly SaleCheckReasonVm[]> {
  if (check === undefined || sale === null) return NO_LINE_REASONS
  const byLine = new Map<string, SaleCheckReasonVm[]>()
  for (const reason of check.reasons) {
    if (!isLineReason(reason)) continue
    const byGood = reason.goodId === null ? -1 : sale.lines.findIndex((line) => sameId(line.productKey, reason.goodId ?? ''))
    const index = byGood >= 0 ? byGood : reason.ordinal !== null ? reason.ordinal - 1 : -1
    const line = sale.lines[index]
    if (line === undefined) continue
    const key = lineKey({ productKey: line.productKey, barcode: line.barcode })
    byLine.set(key, [...(byLine.get(key) ?? []), reasonVm(reason)])
  }
  return byLine.size === 0 ? NO_LINE_REASONS : byLine
}

function checkVm(check: AqsiSaleCheck | undefined): SaleCheckVm | null {
  return check === undefined
    ? null
    : { canSell: check.canSell, summary: check.summary, cartReasons: check.reasons.filter((reason) => !isLineReason(reason)).map(reasonVm) }
}

/** Почему «Продать» выключена, когда служба сказала «нельзя»: слова кассира, коды — только в «Подробнее». */
function checkBlockedText(check: SaleCheckVm, lines: readonly CartLineVm[]): string {
  const names = lines.filter((line) => line.blocked.length > 0).map((line) => `«${line.name}»`)
  const parts: string[] = []
  if (names.length > 0) {
    const admin = lines.some((line) => line.blocked.some((reason) => reason.fixBy === 'administrator'))
    parts.push(
      `В чеке есть товар, который касса не продаст: ${names.join(', ')}. ` +
        (admin ? 'Уберите его из чека или попросите администратора исправить товар.' : 'Уберите его из чека.'),
    )
  }
  for (const reason of check.cartReasons) parts.push(reason.text)
  return parts.length > 0 ? parts.join(' ') : check.summary
}

/** Количество позиции в тысячных — целое: сложение без плавающей точки. */
function thousandths(quantity: string): number | null {
  const value = normalizeQuantity(quantity)
  if (!/^\d{1,5}(?:\.\d{1,3})?$/.test(value)) return null
  const [whole = '0', fraction = ''] = value.split('.')
  return Number.parseInt(whole, 10) * 1000 + Number.parseInt(fraction.padEnd(3, '0'), 10)
}

/** «4 шт», «1,346 кг»: количество складывается, только когда мера у всех позиций одна («3 шт + 0,350 кг» — не число). */
function quantityTotal(lines: readonly CartLineVm[]): string | null {
  const units = new Set(lines.map((line) => line.unit))
  const [unit] = units
  if (lines.length === 0 || units.size !== 1 || unit === null || unit === undefined) return null
  let sum = 0
  for (const line of lines) {
    const value = thousandths(line.quantity)
    if (value === null) return null
    sum += value
  }
  const whole = Math.trunc(sum / 1000)
  const fraction = String(sum % 1000).padStart(3, '0').replace(/0+$/, '')
  return `${whole}${fraction === '' ? '' : `,${fraction}`} ${unit}`
}

function goodAvailability(good: AqsiGood): string | null {
  if (good.invalidReason !== null) return `не по формату в кабинете: ${good.invalidReason}`
  if (good.missingSince !== null) return 'больше нет в кабинете'
  if (good.price === null) return 'без цены в кабинете — продать нельзя'
  return null
}

const EMPTY_GOODS: readonly AqsiGood[] = []

function pickerRow(good: AqsiGood): PickerRowVm {
  const reason = goodAvailability(good)
  return {
    productKey: good.goodId,
    name: good.name,
    sku: good.sku,
    barcode: good.barcodes[0] ?? null,
    // Цена карточки кабинета из снимка — подсказка; цену позиции даёт служба при сохранении.
    priceText: formatPrice(good.price),
    unit: good.unit,
    isWeight: good.isWeight,
    available: reason === null,
    reason,
  }
}

const EMPTY_HITS: readonly CommonGoodHit[] = []

/** Строка выдачи общего каталога: с карточкой кассы — как строка кассы; без неё — «ещё не в кассе» с причиной раздачи дословно. */
function pickerRowOfHit(hit: CommonGoodHit): PickerRowVm {
  if (hit.good !== null) return pickerRow(hit.good)
  const reason = absentReason(hit)
  return {
    productKey: hit.productId,
    name: hit.name,
    sku: hit.sku,
    barcode: hit.matchedBarcode,
    priceText: '',
    unit: null,
    isWeight: null,
    available: false,
    reason,
    absent: { productId: hit.productId, reason },
  }
}

/** Ключ заметки «ещё не в кассе»: по нему действия различают её кнопку и закрытие. */
export const DELIVERY_NOTICE_KEY = 'delivery'
const NO_NOTICES: readonly SaleNoticeVm[] = []

/** «Товар есть в каталоге, но ещё не в кассе AQSI: <причина>» с кнопкой «Отправить в кассу сейчас» (ход отправки — тем же сообщением). */
function deliveryNotices(offer: DeliveryOffer | null): readonly SaleNoticeVm[] {
  if (offer === null) return NO_NOTICES
  if (offer.phase === 'waiting') {
    return [{ key: DELIVERY_NOTICE_KEY, text: `«${offer.name}» отправлен в кассу — ждём его карточку в кассе, затем товар добавится в чек.`, tone: 'neutral', actionLabel: null, busy: true }]
  }
  const text = `«${offer.name}». Товар есть в каталоге, но ещё не в кассе AQSI: ${offer.reason}`
  return [{ key: DELIVERY_NOTICE_KEY, text, tone: 'warning', actionLabel: 'Отправить в кассу сейчас', busy: offer.phase === 'sending' }]
}

function pickerVm(view: QueryView, goods: AqsiGoodsPage | undefined, seat: SaleSeatIntent): PickerVm {
  const ready = readyData(view, goods)
  const search = seat.pickerQuery.trim().toLowerCase()
  const items = ready?.items ?? EMPTY_GOODS
  // Отбирает служба (`search=` по всему снимку, UI-RMK): страница уже отобрана.
  const rows: PickerRowVm[] = items.slice(0, PICKER_PAGE_SIZE).map(pickerRow)
  const total = ready?.total ?? 0
  const page = seat.pickerPage
  return {
    query: view,
    search: seat.pickerQuery,
    rows,
    page,
    canPrev: page > 0,
    canNext: (page + 1) * PICKER_PAGE_SIZE < total,
    totalText:
      ready === undefined
        ? ''
        : search === ''
          ? `Товаров в каталоге кассы: ${total}; страница ${page + 1}`
          : `Найдено: ${total}; страница ${page + 1}`,
  }
}

function payText(sale: AqsiSale | null, paymentKind: SaleSeatIntent['paymentKind']): string {
  const kind = sale?.paymentKind ?? paymentKind
  const total = sale !== null ? ` ${formatKopecks(sale.totalKopecks)}` : ''
  return kind === 'Card' ? `Оплатить картой${total}` : `Принять наличные${total}`
}

/** Отказы службы, после которых человеку нужен следующий шаг, а не только текст. */
const SHIFT_CODES: ReadonlySet<string> = new Set(['shift-not-adopted', 'shift-closed', 'shift-expired'])

function feedbackHintOf(feedback: CommandFeedback | null): FeedbackHintVm | null {
  const code = feedback?.phase === 'rejected' ? feedback.error?.code : undefined
  if (code === undefined || !SHIFT_CODES.has(code)) return null
  return { text: code === 'shift-expired' ? 'Отложите оплаченную продажу, затем закройте и откройте смену: «Действия» → «Смена». Вернитесь к продаже и повторите проведение.' : 'Откройте или примите смену: «Действия» → «Смена».' }
}

/** Подсказки к отказам «чек уже пробит» — сверх дословного текста службы в баннере. */
const OUTSIDE_HINTS: Readonly<Record<string, string>> = {
  'receipt-not-in-journal': 'Сверьте журнал чеков и повторите',
  'receipt-ambiguous': 'Подходит несколько чеков — укажите номер чека кассы из перечисленных выше',
  'operation-unresolved': 'Сначала дождитесь итога операции: проверьте статус или выполните сверку',
}

export function outsideBlockedReason(reason: string, receiptId: string): string | null {
  if (reason.trim() === '') return 'Укажите причину'
  if (receiptId.trim().length > 128) return 'Номер чека — не длиннее 128 символов'
  return null
}

const REFUND_ON_REGISTER = 'На кассе идёт возврат — продажу можно начать после его завершения в окне возврата'

/**
 * Сохранение корзины для экрана (очередь корзины): сохранено / сохраняем, попытка N / нет связи, повторяем. Правка, которую отвергла
 * служба, здесь не говорит: о ней — `cartState` и причина оплаты. Пустая сохранённая корзина — сказать нечего.
 */
function cartSyncOf(draft: CartDraft | undefined, linesCount: number): CartSyncVm | null {
  if (draft === undefined) return linesCount === 0 ? null : { state: 'saved', attempt: null, text: CART_SAVED_TEXT }
  if (draft.phase === 'rejected') return null
  const send = draft.send
  if (send === null || send.attempt === 0) return { state: 'saving', attempt: null, text: CART_SAVING_TEXT }
  if (send.retryAt !== null) return { state: 'retrying', attempt: send.attempt + 1, text: cartRetryText(send) }
  return send.attempt === 1
    ? { state: 'saving', attempt: null, text: CART_SAVING_TEXT }
    : { state: 'saving', attempt: send.attempt, text: cartSavingAttemptText(send.attempt) }
}

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectSaleVm = () => {
  const selectSource = makeSelectSourceCaption()
  const selectRegistersView = makeQueryViewSelector(
    (input: SaleVmInput) => input.mySession,
    () => false,
  )
  const selectSaleReadView = makeQueryViewSelector(
    (input: SaleVmInput) => input.activeSale,
    () => false,
  )
  // Сбой связи показывает общее окно приложения «Восстанавливаем соединение с сервером»; своего восстановления у продажи нет.
  const selectSaleView = selectSaleReadView
  const selectOperationView = makeQueryViewSelector(
    (input: SaleVmInput) => input.operation,
    () => false,
  )
  const selectGoodsView = makeQueryViewSelector(
    (input: SaleVmInput) => input.goods,
    (data) => data.items.length === 0,
  )

  const selectSourceVm = (input: SaleVmInput) =>
    selectSource({ purpose: 'rmk', resourceId: input.resourceId, query: input.sources })
  const selectSalesEnabled = createSelector(
    [(input: SaleVmInput) => (input.sellers?.isError === true ? undefined : input.sellers?.data), (input: SaleVmInput) => input.resourceId],
    (sellers, resourceId) =>
      // Пока список не прочитан — не запрещать: право всё равно проверит служба.
      sellers === undefined || sellers.some((item) => sameId(item.id, resourceId)),
  )
  const selectSeat = createSelector(
    [selectRegistersView, (input: SaleVmInput) => input.mySession.data, (input: SaleVmInput) => input.registers?.data],
    (view, data, registers) => {
      const mine = readyData(view, data)
      if (mine == null) return null
      const register = registers?.registers.find((item) => item.deviceId === mine.deviceId)
      return {
        registerKey: mine.registerKey,
        deviceId: mine.deviceId,
        name: register?.name ?? (mine.deviceSerialNumber !== '' ? `Касса ${mine.deviceSerialNumber}` : 'Касса'),
        sessionVersion: mine.version,
      }
    },
  )
  const selectSale = createSelector(
    [(input: SaleVmInput) => (input.activeSale === undefined ? null : selectSaleView(input)), (input: SaleVmInput) => input.activeSale?.data],
    (view, data) => (view === null ? null : saleOf(view.status === 'unavailable' ? data : readyData(view, data))),
  )
  const selectRefund = createSelector(
    [(input: SaleVmInput) => (input.activeSale === undefined ? null : selectSaleView(input)), (input: SaleVmInput) => input.activeSale?.data],
    (view, data) => {
      const refund = view === null ? null : seatRefundOf(readyData(view, data))
      return refund === null ? null : { refundId: refund.refundId, receiptId: refund.receiptId }
    },
  )
  const selectCheckData = createSelector([(input: SaleVmInput) => input.check?.data, selectSale], currentCheck)
  const selectLineReasons = createSelector([selectCheckData, selectSale], lineReasonsOf)
  const selectCheck = createSelector([selectCheckData], checkVm)
  const selectLines = createSelector(
    [selectSale, (input: SaleVmInput) => input.seat, (input: SaleVmInput) => input.draft, (input: SaleVmInput) => input.goods?.data, selectLineReasons],
    cartLines,
  )
  // Текущая операция продажи: из ответа продажи, дополненная отдельным чтением (RP-PAY-ONE, шаг 6).
  const selectStatus = createSelector([selectSale, (input: SaleVmInput) => input.operation?.data], operationOfSale)
  const selectOperation = createSelector(
    [
      (input: SaleVmInput) => currentOperationOf(input.activeSale?.data),
      (input: SaleVmInput) => (input.operation === undefined ? null : selectOperationView(input)),
      selectStatus,
      (input: SaleVmInput) => saleOf(input.activeSale?.data)?.currentOperation != null,
    ],
    operationVm,
  )
  // Попытка — только ЭТОЙ продажи (или только что закрытой ею): попытка прошлой продажи окна новой не открывает.
  const selectAttempt = createSelector(
    [(input: SaleVmInput) => input.attempt, selectSale, (input: SaleVmInput) => input.seat.finishedSaleId ?? input.fast?.flashSaleId ?? null],
    (attempt, sale, finishedSaleId) => {
      if (attempt === undefined) return null
      // Оплату нажали, пока продажа создавалась: попытка принадлежит продаже, которая вот-вот появится.
      if (attempt.saleId === null) return attempt
      const saleId = sale?.id ?? finishedSaleId
      return saleId !== null && sameId(attempt.saleId, saleId) ? attempt : null
    },
  )
  const selectCycleFeedback = createSelector([(input: SaleVmInput) => input.attemptFeedback, selectAttempt], (feedback, attempt) =>
    attempt === null ? null : (feedback ?? null),
  )
  const selectPicker = createSelector(
    [selectGoodsView, (input: SaleVmInput) => input.goods?.data, (input: SaleVmInput) => input.seat],
    pickerVm,
  )
  const selectFinished = createSelector(
    [(input: SaleVmInput) => input.seat.finishedSaleId, (input: SaleVmInput) => input.finishedSale?.data],
    (saleId, sale) =>
      saleId === null || sale === undefined || !sameId(sale.id, saleId)
        ? null
        : {
            receiptId: sale.receiptId,
            totalText: formatKopecks(sale.totalKopecks),
            paymentText: sale.paymentKind === 'Card' ? 'картой' : 'наличными',
            issuedOutside: sale.fiscal === 'IssuedOutside',
          },
  )
  // Быстрая оплата: продажа только что ушла в фон — итог для галочки «Оплата прошла». Чтение продажи может ещё не прийти: тогда без суммы.
  const selectHandedOff = createSelector(
    [(input: SaleVmInput) => input.fast?.flashSaleId ?? null, (input: SaleVmInput) => input.finishedSale?.data],
    (saleId, sale): HandedOffVm | null =>
      saleId === null
        ? null
        : {
            saleId,
            totalText: sale !== undefined && sameId(sale.id, saleId) ? formatKopecks(sale.totalKopecks) : '',
          },
  )
  const selectCommand = createSelector([(input: SaleVmInput) => input.command], commandVm)

  const selectBase = createSelector(
    [
      (input: SaleVmInput) => input.resourceId,
      (input: SaleVmInput) => input.seat,
      (input: SaleVmInput) => input.draft,
      (input: SaleVmInput) => input.command,
      (input: SaleVmInput) => input.feedback ?? null,
      selectSourceVm,
      selectSalesEnabled,
      selectRegistersView,
      selectSeat,
      (input: SaleVmInput) => (input.activeSale === undefined ? null : selectSaleView(input)),
      selectSale,
      selectLines,
      selectOperation,
      selectCommand,
      selectFinished,
      selectPicker,
      selectRefund,
      selectCycleFeedback,
      (input: SaleVmInput) => input.activeSale?.data !== undefined,
    ],
    (
      resourceId,
      intent,
      draft,
      pending,
      feedback,
      source,
      salesEnabled,
      registersQuery,
      seat,
      saleQuery,
      sale,
      lines,
      operation,
      command,
      finished,
      picker,
      activeRefund,
      cycle,
      hasSaleData,
    ): SaleBaseVm => {
      const blocked = blocksSaleCommands(pending)
      // Пока продажа места не прочитана ни разу (F5 без ответа) корзина заперта: иначе пустая «новая» корзина спрятала бы серверную
      // продажу с неизвестным исходом. Перечитывание (опрос, живое событие) и сбой связи при уже прочитанной продаже корзину НЕ запирают:
      // кассир набирает чек и оплачивает, пока сервер уточняется в фоне (RP-SALE-FAST, шаг 4, К-106).
      const known = hasSaleData && saleQuery !== null && (saleQuery.status === 'ready' || saleQuery.status === 'unavailable' || saleQuery.status === 'error')
      // Оплата уже нажата: пока она готовится (сохраняем корзину, проверяем связь) и идёт, корзина не меняется.
      const paymentStarting = cycle !== null && cycle.phase === 'sending' && PREPARE_COMMANDS.has(cycle.command)
      // Возврат на кассе места (C4b): служба держит одно незавершённое на кассу — новую продажу не начать.
      const editable = known && activeRefund === null && (sale === null || canEditCart(sale))
      const card = (sale?.paymentKind ?? intent.paymentKind) === 'Card'
      // Этапы — как у службы: оплатить можно до денег и чека; пробить чек — после денег (или доказанного отказа чека).
      const payStage =
        sale !== null &&
        !sale.isFinished &&
        (card
          ? ['Pending', 'Failed'].includes(sale.payment) && sale.fiscal === 'Pending'
          : sale.payment === 'NotRequired' && sale.fiscal === 'Pending')
      const fiscalizeStage =
        sale !== null &&
        !sale.isFinished &&
        ['Succeeded', 'NotRequired'].includes(sale.payment) &&
        (card ? ['Pending', 'Failed'].includes(sale.fiscal) : sale.fiscal === 'Failed')
      // Деньги и чек (привязать чек, пробить, отложить) — только по свежему ответу службы: после сбоя чтения решает повторное чтение.
      const fresh = saleQuery?.status === 'ready'
      const ready = fresh && seat !== null && !blocked && draft === undefined
      // «Привязать уже пробитый чек»: деньги подтверждены, чек программой не пробит и не в пути.
      // При Unknown/InProgress чека кнопки нет — исход решает сверка операции.
      const outsideStage =
        sale !== null &&
        !sale.isFinished &&
        (card ? sale.payment === 'Succeeded' : sale.payment === 'NotRequired') &&
        ['Pending', 'Failed'].includes(sale.fiscal)
      const outsideDraft =
        intent.outside !== null && sale !== null && sameId(intent.outside.saleId, sale.id) && outsideStage
          ? intent.outside
          : null
      const outsidePending = pending?.kind === 'outside' && pending.phase === 'sending'
      // «Чек уже пробит» — команда цикла оплаты: её отказ — в попытке, не в общем итоге кассы.
      const outsideCode =
        cycle?.command === 'sale.outside' && cycle.phase === 'rejected' ? (cycle.error?.code ?? null) : null
      const outsideBlocked =
        outsideDraft === null
          ? null
          : outsidePending
            ? 'Служба ищет чек…'
            : !fresh
              ? 'Ждём свежий ответ службы о продаже'
              : blocked
                ? 'Исход прошлой команды неизвестен — сначала проверьте статус'
                : outsideBlockedReason(outsideDraft.reason, outsideDraft.receiptId)
      const outside: OutsideReceiptVm | null =
        outsideDraft === null
          ? null
          : {
              reason: outsideDraft.reason,
              receiptId: outsideDraft.receiptId,
              canSubmit: outsideBlocked === null,
              submitBlockedReason: outsideBlocked,
              sending: outsidePending,
              hint: outsideCode !== null ? (OUTSIDE_HINTS[outsideCode] ?? null) : null,
              offerJournalSync: outsideCode === 'receipt-not-in-journal',
            }
      const cartSync = cartSyncOf(draft, lines.length)
      // Продажи ещё нет, а позиции в очереди: оплата дождётся создания продажи.
      const creating = sale === null && draft !== undefined && lines.length > 0
      let payBlockedReason: string | null = null
      if (!salesEnabled) payBlockedReason = 'Продажа в этом кабинете вам недоступна (право sales.manage не включено у кабинета или не выдано вам)'
      else if (seat === null) payBlockedReason = 'Начните работу за кассой'
      else if (activeRefund !== null) payBlockedReason = REFUND_ON_REGISTER
      else if (blocked) payBlockedReason = 'Исход прошлой команды неизвестен — сначала проверьте статус'
      // Несохранённая корзина оплату НЕ выключает: нажатие дождётся очереди (окно «Сохраняем корзину…»). Выключает только корзина, которую
      // служба не приняла, и ошибка в строке — их исправляет человек.
      else if ((draft !== undefined && draft.phase === 'rejected') || lines.some((line) => line.quantityError !== null || line.priceError !== null)) {
        payBlockedReason = 'Исправьте корзину: служба её не приняла'
      } else if (lines.length === 0) payBlockedReason = 'Добавьте товар'
      const started = sale !== null && (sale.payment !== 'Pending' || sale.fiscal !== 'Pending')
      const lockedReason = !known
        ? 'Продажа кассы ещё не прочитана с сервера — корзина откроется после чтения'
        : activeRefund !== null
          ? REFUND_ON_REGISTER
          : sale !== null && !canEditCart(sale)
            ? 'Оплата или чек уже начаты — корзину менять нельзя'
            : blocked
              ? 'Ждём исхода команды — корзину менять нельзя'
              : paymentStarting
                ? 'Идёт оплата — корзину менять нельзя'
                : null
      return {
        resourceId,
        source,
        placeReady: seat !== null,
        salesEnabled,
        registersQuery,
        seat,
        saleQuery,
        saleId: sale?.id ?? null,
        saleVersion: sale?.version ?? null,
        paymentKind: sale?.paymentKind ?? intent.paymentKind,
        canChoosePaymentKind: ready && editable,
        lines,
        // Правка ещё не сохранена: итог — сумма строк, которые уже посчитаны, а ответ службы его подтвердит (RP-FB15).
        totalText: formatKopecks(draftTotalKopecks(draft !== undefined, lines) ?? sale?.totalKopecks ?? 0),
        totalPending: draft !== undefined,
        cartState:
          draft !== undefined
            ? draft.phase === 'saving' && draft.send?.retryAt != null
              ? 'retrying'
              : draft.phase
            : lines.length === 0
              ? 'empty'
              : 'synced',
        cartSync,
        cartLocked: !editable || blocked || paymentStarting,
        cartLockedReason: lockedReason,
        money: started ? { label: 'Деньги', ...MONEY[sale.payment] } : null,
        receipt: started ? { label: 'Чек', ...RECEIPT[sale.fiscal] } : null,
        lastMessage: sale?.lastMessage ?? null,
        canPay: known && (payStage || creating) && payBlockedReason === null,
        payText: payText(sale, intent.paymentKind),
        // Причину показываем, пока оплата — следующий шаг; на других этапах кнопки нет.
        payBlockedReason: payStage || sale === null ? payBlockedReason : null,
        canFiscalize: fiscalizeStage && ready,
        canCancel: sale !== null && canEditCart(sale) && ready,
        operation,
        command,
        finished,
        canMarkIssuedOutside: outsideStage && ready && outside === null,
        outside,
        feedbackHint: feedbackHintOf(feedback),
        activeRefund,
        picker,
        feedback,
      }
    },
  )

  // ——— UI-RMK: строка поиска, отложенные, оплата одним нажатием, ход оплаты ———
  const selectSearchView = makeQueryViewSelector(
    (input: SaleVmInput) => input.searchGoods,
    () => false,
  )
  const selectSearch = createSelector(
    [
      (input: SaleVmInput) => input.seat.searchText,
      (input: SaleVmInput) => input.searchedText ?? '',
      (input: SaleVmInput) => (input.searchGoods === undefined ? null : selectSearchView(input)),
      (input: SaleVmInput) => input.searchGoods?.data,
      (input: SaleVmInput) => input.seat.lookups,
    ],
    searchVm,
  )
  const selectHeldView = makeQueryViewSelector(
    (input: SaleVmInput) => input.heldCarts,
    (data) => data.carts.length === 0,
  )
  const selectHeld = createSelector(
    [selectHeldView, (input: SaleVmInput) => input.heldCarts?.data],
    (query, data): HeldVm => ({ query, carts: (readyData(query, data)?.carts ?? EMPTY_HELD).map(heldCartVm) }),
  )
  const selectPricesAt = createSelector(
    [(input: SaleVmInput) => input.snapshot?.data?.materialization?.completedAt ?? null],
    pricesAt,
  )

  const selectNow = (input: SaleVmInput) => input.now ?? Math.floor(Date.now() / CLOCK_BUCKET_MS) * CLOCK_BUCKET_MS
  const selectFinishedAttention = createSelector(
    [(input: SaleVmInput) => input.seat.finishedSaleId, (input: SaleVmInput) => input.finishedSale?.data],
    (saleId, sale) => (saleId === null || sale === undefined || !sameId(sale.id, saleId) ? EMPTY_ATTENTION : sale.attention),
  )

  const selectPriceRight = createSelector(
    [(input: SaleVmInput) => input.priceEditors, (input: SaleVmInput) => input.resourceId],
    (editors, resourceId) =>
      editors === undefined || (editors.data?.some((item) => sameId(item.id, resourceId)) ?? false),
  )

  return createSelector(
    [
      selectBase,
      selectSale,
      selectLines,
      (input: SaleVmInput) => input.seat,
      (input: SaleVmInput) => input.draft,
      selectSearch,
      selectHeld,
      selectPricesAt,
      selectCheck,
      selectPriceRight,
      selectStatus,
      selectNow,
      selectCycleFeedback,
      selectFinishedAttention,
      (input: SaleVmInput) => (input.parkedCount?.isSuccess === true ? input.parkedCount.data : undefined),
      (input: SaleVmInput) => input.backgroundSales,
      selectAttempt,
      (input: SaleVmInput) => input.command,
      (input: SaleVmInput) => input.resolveDraft,
      selectHandedOff,
      (input: SaleVmInput) => input.fast?.enabled === true,
      (input: SaleVmInput) => input.aqsiState,
    ],
    (
      base,
      sale,
      lines,
      seat,
      draft,
      search,
      held,
      pricesAtText,
      check,
      priceRight,
      status,
      now,
      cycle,
      finishedAttention,
      parkedCount,
      backgroundSales,
      attempt,
      pending,
      resolveDraft,
      handedOff,
      fastEnabled,
      aqsiState,
    ): AqsiSaleVm => {
      // Команда в пути или с неизвестным исходом держит корзину (как `blocksSaleCommands`).
      const blocked = base.command !== null && base.command.phase !== 'not-arrived'
      // Корзину можно менять и продажа не идёт — тогда работают «Отложить», «Очистить», «Покупателю» и обе оплаты.
      const quiet = base.seat !== null && !base.cartLocked && draft === undefined && !blocked && base.activeRefund === null
      const editableSale = sale !== null && canEditCart(sale)
      // Служба проверила корзину и сказала «нельзя» (С2): «Продать» выключена, причины — словами. Без ответа проверки не блокируем.
      // Проверка относится к сохранённой версии корзины: пока в очереди есть несохранённая правка, она ничего не доказывает.
      const unsellable = check !== null && !check.canSell && draft === undefined
      // Фоновые оплаты (отложенные чеки) и смена оплату не выключают, а уточняются в фоне: служба не пустит вторую неразрешённую операцию на
      // кассу и ответит словами. Кассиру о них говорит строка блока состояния.
      const backgroundUnknown = backgroundSales !== undefined && (backgroundSales.data === undefined || backgroundSales.isError || backgroundSales.isUninitialized)
      const checkingPayment = backgroundSales?.data?.some((row) => row.isParked && !row.isFinished && ['InProgress', 'Unknown'].includes(row.payment)) === true
      // Быстрая оплата: чек предыдущей продажи печатается в фоне - оплата дождётся его конца. Проблемный чек (отказ, ручная сверка) - обычный долг в
      // «Отложенных» и оплату не закрывает (решение владельца 18.10.2026, RP-FB35 §15); служба закрывает оплату только пока чек живой у кассы.
      const ahead = receiptsAheadOf(fastEnabled ? backgroundSales?.data : undefined, now)
      const backgroundNote = checkingPayment
        ? 'В фоне проверяем предыдущую оплату. Оплату можно начать: касса не примет вторую, пока предыдущая не разрешена.'
        : ahead.printing > 0
          ? RECEIPT_AHEAD_STATUS_TEXT
          : backgroundUnknown
            ? 'Фоновые оплаты уточняются. Оплату можно начать.'
            : null
      // Оплата: службу устраивает право, касса, корзина без отказа и проверка; несохранённая правка и перечитывание не мешают.
      const payable = base.canPay && !unsellable
      const holdable = quiet && base.salesEnabled && editableSale && lines.length > 0 && !unsellable
      const kind = sale?.paymentKind ?? seat.paymentKind
      // Оплата уходит: сама команда — или шаг до неё (проверка связи с кассой, смена способа оплаты). Кнопка крутится и не нажимается
      // второй раз; ход — в окне оплаты (решение владельца 23.10.2026: при оплате — только окно по центру).
      const starting = cycle !== null && cycle.phase === 'sending' && PREPARE_COMMANDS.has(cycle.command)
      const paying = (base.command?.kindText === 'Оплата' && base.command.phase === 'sending') || starting
      const ready = base.saleQuery?.status === 'ready' && base.seat !== null && !blocked && draft === undefined
      // Фоновое чтение не отменяет известный статус принятой оплаты. Иначе кнопка и её подтверждение исчезают каждый тик.
      // При ошибке чтения, смене операции или неизвестном исходе команды защита остаётся.
      const cancelReady = base.saleQuery?.status === 'ready' && base.seat !== null && !blocked && draft === undefined
      const retry = retryVmOf(sale, seat, ready, blocked, cycle, now)
      const park = parkVmOf(sale, seat, cancelReady, blocked, cycle)
      const latePayment = latePaymentVmOf(sale, cancelReady, blocked, cycle)
      const payBlockedReason =
        base.payBlockedReason ??
        (unsellable && lines.length > 0 && editableSale ? checkBlockedText(check, lines) : null)
      const canPayNow = payable && !starting
      // Деньги взяты, исход чека не известен: служба сама читает AQSI, кассир видит что нашли и одно действие (RP-CASH-RECOVERY §4.1).
      const recovery = needsAqsiCheck(sale, status)
        ? recoveryPanelVmOf({
            state: aqsiState,
            fetching: aqsiState === undefined || aqsiState.isUninitialized || aqsiState.isLoading,
            cycle,
            canAct: cancelReady,
            // Служба сама решить не может — две галочки сверки чека (К-128), предзаполненные тем, что она прочитала в AQSI.
            verdict: receiptVerdictOf({
              sale,
              status,
              draft: resolveDraft ?? EMPTY_RESOLVE_DRAFT,
              allowed: base.operation?.manualRequired === true && base.operation.version !== null,
              seatKnown: base.seat !== null,
              prefill: aqsiState?.data?.prefill,
              numberRequired: receiptNumberRequiredBy(cycle),
            }),
            // «Повторить оплату» панели — тот же путь, что «Картой»: допускает его та же проверка (`canPayNow`).
            payment: { canPay: canPayNow, blockedReason: canPayNow ? null : payBlockedReason },
          })
        : null
      const stage = paymentStageOf({
        sale,
        finished: base.finished !== null,
        handedOff: handedOff !== null,
        operation: status,
        command: pending,
        attempt,
        feedback: cycle,
        now,
      })
      const payment =
        stage === null
          ? null
          : paymentFlowOf(stage, {
              base,
              sale,
              status,
              now,
              attempt,
              cycle,
              pending,
              retry,
              park: recovery === null ? park : panelParkOf(park, aqsiState?.data),
              latePayment,
              canPayNow,
              payBlockedReason,
              cancelReady,
              resolveDraft,
              finishedAttention,
              kind,
              finishedSaleId: seat.finishedSaleId ?? handedOff?.saleId ?? null,
              handedOff,
              recovery,
            })
      // Без окна оплаты — строкой блока состояния: прошлый отказ (после F5 окно не навязывается, вопрос 1) и предупреждения службы.
      // Запоздавший слип показывает своя строка с кнопкой в окно, второй раз его не повторяем.
      const warnings = attentionLines((sale?.attention ?? finishedAttention).filter((item) => item.code !== LATE_PAYMENT_SLIP))
      const lastDecline =
        payment === null &&
        sale !== null &&
        !sale.isFinished &&
        sale.paymentKind === 'Card' &&
        sale.payment === 'Failed' &&
        sale.fiscal === 'Pending' &&
        !hasLateSlip(sale) &&
        attempt?.cancelledByCashier !== true
          ? lastDeclineText(sale)
          : null
      return {
        ...base,
        canPay: payable,
        payBlockedReason,
        check,
        customerContact: draft?.contact ?? sale?.customerContact ?? '',
        canEditContact: base.seat !== null && !base.cartLocked && !blocked && sale !== null,
        canEditPrice: priceRight && base.seat !== null && !base.cartLocked,
        selectedLine: lines.find((line) => line.selected) ?? null,
        linesText: `Позиций: ${lines.length}`,
        quantityTotalText: quantityTotal(lines),
        search,
        notices: deliveryNotices(seat.delivery),
        held,
        canHold: holdable,
        canClear: quiet && editableSale,
        payButtons: PAY_KINDS.map(
          (payKind): PayButtonVm => ({
            kind: payKind,
            label: payKind === 'Cash' ? 'Наличными' : 'Картой',
            enabled: canPayNow,
            primary: payKind === kind,
            busy: paying && payKind === kind,
          }),
        ),
        payment,
        retry,
        park,
        // Поздний слип — одно место: окно, если оно его показывает; иначе строка блока состояния, открывающая окно.
        latePayment: stage === 'late-slip' ? null : latePayment,
        parkedNotice: parkedNoticeOf(parkedCount),
        receiptAhead: ahead.printing > 0,
        recoverAction: recovery === null ? null : sendableAction(aqsiState?.data),
        recoverRetriesPayment: recovery !== null && isRetryPayment(aqsiState?.data),
        pricesAtText,
        paymentStatus:
          [lastDecline, ...(payment === null || payment.collapsed ? warnings : []), payment === null ? backgroundNote : null]
            .filter((part): part is string => part !== null)
            .join(' ') || null,
      }
    },
  )
}

/** Поля vm продажи, которые считает первая ступень селектора (до РМК-надстройки). */
type SaleBaseVm = Omit<
  AqsiSaleVm,
  | 'check'
  | 'customerContact'
  | 'canEditContact'
  | 'canEditPrice'
  | 'selectedLine'
  | 'linesText'
  | 'quantityTotalText'
  | 'search'
  | 'held'
  | 'canHold'
  | 'canClear'
  | 'payButtons'
  | 'payment'
  | 'retry'
  | 'park'
  | 'parkedNotice'
  | 'pricesAtText'
  | 'paymentStatus'
  | 'receiptAhead'
>

const PAY_KINDS: readonly PaymentKind[] = ['Cash', 'Card']
const EMPTY_HELD: readonly AqsiHeldCart[] = []
const EMPTY_ATTENTION: readonly AqsiSaleAttention[] = []
const EMPTY_ROWS: readonly PickerRowVm[] = []

/** Сбой связи (нет ответа), а не отказ службы: поиск повторяется сам, «ничего не найдено» тут сказать нельзя. */
const NO_ANSWER_KINDS: ReadonlySet<string> = new Set(['network', 'timeout', 'unavailable', 'unknown-outcome'])

function searchVm(
  text: string,
  searched: string,
  view: QueryView | null,
  data: CommonGoodsPage | undefined,
  lookups: readonly PendingLookup[],
): SearchVm {
  const pending = lookups.map((item) => ({ id: item.id, text: item.text, statusText: lookupRetryText(item.attempt) }))
  if (text.trim() === '') return { text, rows: EMPTY_ROWS, status: 'idle', errorText: null, pending }
  // Выдача относится к другому тексту (пауза ввода) или ещё не пришла — «ищем», старые строки не показываем.
  if (view === null || searched.trim() !== text.trim() || view.status === 'loading' || view.status === 'idle') {
    return { text, rows: EMPTY_ROWS, status: 'waiting', errorText: null, pending }
  }
  if (view.status !== 'ready' && view.status !== 'empty') {
    // Нет связи — честный статус и повтор, а не пустая выдача; отказ службы — её слова.
    if (view.error !== null && NO_ANSWER_KINDS.has(view.error.kind)) {
      return { text, rows: EMPTY_ROWS, status: 'retrying', errorText: SEARCH_RETRY_TEXT, pending }
    }
    return { text, rows: EMPTY_ROWS, status: 'error', errorText: view.error?.message ?? 'Поиск недоступен', pending }
  }
  return { text, rows: (readyData(view, data)?.hits ?? EMPTY_HITS).map(pickerRowOfHit), status: 'ready', errorText: null, pending }
}

function heldCartVm(cart: AqsiHeldCart): HeldCartVm {
  const names = cart.lines.slice(0, 2).map((line) => line.name)
  const more = cart.lines.length - names.length
  return {
    id: cart.id,
    version: cart.version,
    timeText: formatClock(cart.heldAt) ?? formatMoment(cart.heldAt),
    linesText: `${cart.lines.length} поз.`,
    totalText: formatKopecks(cart.totalKopecks),
    preview: more > 0 ? `${names.join(', ')} и ещё ${more}` : names.join(', '),
    heldByName: cart.heldByName,
    contact: cart.customerContact,
  }
}

/** «Цены кассы на 14:32» (сегодня) или «на 07.10 14:32»: цену в чеке даёт снимок каталога кабинета этого времени. */
function pricesAt(completedAt: string | null): string | null {
  if (completedAt === null) return null
  const moment = new Date(completedAt)
  if (Number.isNaN(moment.getTime())) return null
  const today = new Date().toDateString() === moment.toDateString()
  return `Цены кассы на ${today ? (formatClock(completedAt) ?? '') : formatMoment(completedAt)}`
}

/** Отказы фискального профиля на оплате (422 до денег): код службы только в «Подробнее» баннера, на экране — слова. */
const FISCAL_REFUSALS: ReadonlySet<string> = new Set([
  'fiscal-profile-blocked',
  'subject-missing',
  'subject-not-mapped',
  'payment-method-missing',
  'payment-method-not-configured',
  'tax-missing',
  'tax-not-mapped',
  'unit-missing',
  'unit-not-mapped',
  'weight-inconsistent',
  'price-missing',
  'good-deleted',
  'good-missing-in-cabinet',
  'good-other-configuration',
  'good-unknown',
  'good-invalid-native-data',
])

/** «Прошлая оплата не прошла: …» — строка блока состояния, когда окна нет (после F5 или после «Закрыть»). Причина кассы — дословно. */
function lastDeclineText(sale: AqsiSale): string {
  const reason = sale.lastMessage?.trim().replace(/[.\s]+$/, '') ?? ''
  return `Прошлая оплата не прошла${reason === '' ? '' : `: ${reason}`}. Деньги не списаны.`
}

/** Подсказки к отказам команд оплаты — сверх дословного текста службы. */
const PAYMENT_HINTS: Readonly<Record<string, string>> = {
  'shift-closed': 'Откройте или примите смену: «Действия» → «Смена».',
  'shift-not-adopted': 'Откройте или примите смену: «Действия» → «Смена».',
  'shift-expired':
    'Отложите оплаченную продажу, затем закройте и откройте смену: «Действия» → «Смена». Вернитесь к продаже и повторите проведение.',
  'receipt-not-in-journal': 'Сверьте журнал чеков и повторите',
  'receipt-ambiguous': 'Подходит несколько чеков — укажите номер чека кассы из перечисленных выше',
  'operation-unresolved': 'Сначала дождитесь итога операции: проверьте статус или выполните сверку',
  // «Пробить чек» (К-128): служба заново читает кассу и отказывает словами AQSI — ниже только что делать.
  'payment-required': 'Деньги по продаже не подтверждены — чек не пробивается. Проверьте оплату на терминале.',
  'receipt-printing': 'Касса печатает чек этой продажи — дождитесь конца печати и проверьте статус.',
}

/** Итоги «Готово», которые окну есть что сказать (остальные видны по этапу). */
const DONE_SHOWN: ReadonlySet<string> = new Set([
  'sale.cancel-payment',
  'sale.retry',
  'sale.recover',
  'sale.late-payment',
  'receipts.journal-sync',
  'operation.resolve',
  'sale.reconcile',
])

/** Ответ последней команды цикла — в окне оплаты (RP-PAY-ONE, шаг 5): отказ дословно, «ответ не получен», полезный итог. */
export function paymentNoticeOf(feedback: CommandFeedback | null): PaymentNoticeVm | null {
  if (feedback === null) return null
  const meta = feedback.error === null ? null : `Код: ${feedback.error.code}${feedback.error.correlationId === null ? '' : ` · запрос ${feedback.error.correlationId}`}`
  if (feedback.phase === 'rejected') {
    const text = feedback.error?.message ?? feedback.note ?? ''
    if (text === '') return null
    const hint = feedback.error === null ? null : (PAYMENT_HINTS[feedback.error.code] ?? feedback.note)
    return { tone: 'danger', title: 'Команда не выполнена', text, hint, meta }
  }
  if (feedback.phase === 'unknown') {
    return { tone: 'warning', title: 'Ответ не получен', text: feedback.note ?? feedback.error?.message ?? 'Неизвестно, дошла ли команда.', hint: null, meta }
  }
  if (feedback.phase === 'done' && feedback.note !== null && DONE_SHOWN.has(feedback.command)) {
    return { tone: 'neutral', title: 'Готово', text: feedback.note, hint: null, meta: null }
  }
  return null
}

/** Что нужно окну оплаты, кроме этапа: продажа, операция, попытка и уже собранные части vm. */
interface FlowContext {
  base: SaleBaseVm
  sale: AqsiSale | null
  status: AqsiSaleOperation | undefined
  now: number
  attempt: PaymentAttempt | null
  cycle: CommandFeedback | null
  pending: SaleCommand | undefined
  retry: ReceiptRetryVm | null
  park: ParkVm | null
  latePayment: LatePaymentVm | null
  canPayNow: boolean
  payBlockedReason: string | null
  cancelReady: boolean
  resolveDraft: ResolveDraft | undefined
  finishedAttention: readonly AqsiSaleAttention[]
  kind: PaymentKind
  /** Продажа, закрытая чеком в этой вкладке: окно итога — с тем же ключом, что и ход. */
  finishedSaleId: string | null
  /** Быстрая оплата: продажа только что ушла в фон; `null` — нет. */
  handedOff: HandedOffVm | null
  /** Панель «Проверка в AQSI»: исход чека выясняет служба; `null` — панель не нужна. */
  recovery: RecoveryPanelVm | null
}

/** Продажа, ушедшая в фон по быстрой оплате: для галочки «Оплата прошла». */
interface HandedOffVm {
  saleId: string
  /** Сумма продажи; пусто, пока её чтение не пришло. */
  totalText: string
}

/** Этапы, где ход идёт сам: окно можно свернуть. */
const COLLAPSIBLE = COLLAPSIBLE_PAYMENT_STAGES

/**
 * Форма ручной сверки окна оплаты — по текущей операции продажи и черновику. Операция чека сверяется двумя галочками (К-128): итога,
 * причины и «что проверено» у неё нет.
 */
function resolveFormOf(
  operation: SaleOperationVm | null,
  draft: ResolveDraft,
  status: AqsiSaleOperation | undefined,
  sale: AqsiSale | null,
  seatKnown: boolean,
  feedback: CommandFeedback | null,
): PaymentResolveVm {
  const resultIdRequired = status !== undefined && isSlipOperation(status.kind)
  const allowed = operation?.manualRequired === true && operation.version !== null
  const verdict = receiptVerdictOf({ sale, status, draft, allowed, seatKnown, numberRequired: receiptNumberRequiredBy(feedback) })
  const blocked = verdict === null ? resolveBlockedReason(draft, resultIdRequired) : verdict.blockedReason
  return {
    kindText: operation?.kindText ?? 'Операция',
    stateText: operation?.stateText ?? '',
    details: operation?.details ?? [],
    allowed,
    allowedReason: allowed
      ? null
      : operation?.manualRequired === true
        ? 'Читаем операцию у службы — форма откроется после чтения.'
        : 'Итог операции уже известен — сверять нечего',
    outcome: draft.outcome,
    reason: draft.reason,
    checked: draft.checked,
    resultId: draft.resultId,
    resultIdRequired,
    canSubmit: verdict === null && allowed && blocked === null,
    submitBlockedReason: allowed && verdict === null ? blocked : null,
    sending: draft.phase === 'sending',
    done: draft.phase === 'done',
    verdict,
  }
}

/** Значения окна, которых нет в содержимом хода: по умолчанию кнопок нет. */
function flowDefaults(stage: PaymentStage, context: FlowContext): Omit<PaymentFlowVm, keyof ProgressVm | 'stage' | 'attemptKey'> {
  const cancel = context.cycle?.command === 'sale.cancel-payment' ? context.cycle.phase : null
  return {
    waitingSince: null,
    collapsed: context.attempt?.collapsed === true && COLLAPSIBLE.has(stage),
    canCollapse: COLLAPSIBLE.has(stage),
    cancelling: cancel === 'sending' || (cancel === 'done' && stage === 'terminal'),
    // «Отменить» нажато без связи: отмена в очереди, уйдёт тем же ключом (RP-SALE-FAST, шаг 6).
    cancelQueued: context.attempt?.cancelQueued === true,
    repeat: null,
    alternative: null,
    canClose: false,
    latePayment: null,
    resolve: null,
    done: null,
    notice: paymentNoticeOf(context.cycle),
  }
}

/**
 * Окно оплаты для этапа (RP-PAY-ONE): содержимое хода (тексты, отметки, кнопки чека) — `progressVm`, этап и кнопки попытки — здесь. Ключ
 * монтирования — попытка; без записанной попытки (F5, другая вкладка) — продажа, тем же ключом, каким попытку подхватит модуль.
 */
function paymentFlowOf(stage: PaymentStage, context: FlowContext): PaymentFlowVm {
  const { base, sale, status, now, attempt, cycle, pending } = context
  const saleId = sale?.id ?? context.finishedSaleId ?? ''
  const key = attempt?.id ?? `sale:${saleId.toLowerCase()}`
  const defaults = flowDefaults(stage, context)
  const operation = base.operation
  const details = [
    ...(operation?.details ?? []),
    ...(operation !== null ? [`${operation.kindText}: ${operation.stateText}`, `Номер операции: ${operation.operationId}`] : []),
  ]
  const amountText = sale !== null ? formatKopecks(sale.totalKopecks) : base.totalText
  const steps = stepsOf(sale, base.command, now, status)
  const plain = {
    ...defaults,
    attemptKey: key,
    stage,
    steps,
    amountText,
    details,
    watchNote: null,
    waiting: false,
    canCancelPayment: false,
    manualRequired: false,
    canFiscalize: false,
    canMarkOutside: false,
    retry: null,
    park: null,
    warnings: [] as readonly string[],
  }
  switch (stage) {
    case 'done': {
      if (context.handedOff !== null) {
        // Быстрая оплата: деньги взяты, чек печатается в фоне. Слов «Чек пробит» здесь нет — он ещё не пробит.
        return {
          ...plain,
          canCollapse: false,
          collapsed: false,
          title: 'Оплата прошла',
          text: FAST_HAND_OFF_DONE_TEXT,
          tone: 'success',
          amountText: context.handedOff.totalText,
          steps: [
            { key: 'payment', label: '① Оплата (терминал)', state: 'done', text: 'оплачено' },
            { key: 'receipt', label: '② Чек (фискальный накопитель)', state: 'active', text: 'печатается в фоне' },
          ],
          details: [],
          warnings: [],
          notice: null,
          done: { receiptId: null, text: FAST_HAND_OFF_DONE_TEXT, autoClose: true, announce: 'Оплата прошла, чек печатается в фоне' },
        }
      }
      const finished = base.finished
      const warnings = attentionLines(context.finishedAttention)
      const outside = finished?.issuedOutside === true
      const text = `${finished?.receiptId != null ? `№ ${finished.receiptId} · ` : ''}${finished?.totalText ?? ''} ${finished?.paymentText ?? ''}. Можно обслуживать следующего покупателя.`
      return {
        ...plain,
        canCollapse: false,
        collapsed: false,
        title: outside ? 'Продажа закрыта чеком с кассы' : 'Чек пробит',
        text,
        tone: 'success',
        amountText: finished?.totalText ?? amountText,
        steps: [
          { key: 'payment', label: finished?.paymentText === 'наличными' ? 'Наличные получены' : '① Оплата (терминал)', state: 'done', text: finished?.paymentText === 'наличными' ? 'получены' : 'оплачено' },
          { key: 'receipt', label: '② Чек (фискальный накопитель)', state: 'done', text: 'пробит' },
        ],
        details: [],
        warnings,
        notice: null,
        done: { receiptId: finished?.receiptId ?? null, text, autoClose: !outside && warnings.length === 0 },
      }
    }
    case 'saving': {
      // Оплату нажали, а корзина ещё не сохранена: ждём очередь (RP-SALE-FAST, шаг 4). Слова — живые: попытка и «нет связи» берутся из очереди.
      const retrying = base.cartSync?.state === 'retrying'
      if (cycle?.command === WAIT_RECEIPT_COMMAND) {
        // Быстрая оплата: касса не примет новую операцию, пока чек предыдущей продажи печатается. «Отменить» безопасна: деньги не уходили.
        return {
          ...plain,
          title: 'Ждём конца печати чека',
          text: cycle.note ?? PAY_WAITING_RECEIPT_TEXT,
          tone: 'neutral',
          waiting: true,
          steps: steps.map((step) => ({ ...step, state: 'waiting' as const, text: step.key === 'payment' ? 'после печати чека предыдущей продажи' : 'после оплаты' })),
          canCollapse: false,
          collapsed: false,
          canCancelPreparation: true,
        }
      }
      return {
        ...plain,
        title: 'Сохраняем корзину',
        text: base.cartSync?.text ?? CART_SAVING_TEXT,
        tone: retrying ? 'warning' : 'neutral',
        waiting: !retrying,
        steps: steps.map((step) => ({ ...step, state: 'waiting' as const, text: step.key === 'payment' ? 'после сохранения корзины' : 'после оплаты' })),
        canCollapse: false,
        collapsed: false,
        canCancelPreparation: true,
      }
    }
    case 'declined':
      return {
        ...plain,
        title: 'Не оплачено',
        text: [sale?.lastMessage ?? null, 'Деньги не списаны.'].filter((line): line is string => line !== null && line.trim() !== '').join('\n'),
        tone: 'danger',
        steps: steps.map((step) => (step.key === 'receipt' ? { ...step, text: 'не будет' } : step)),
        // «Повторить оплату» — тот же путь, что «Картой»: проверка связи → смена способа → оплата.
        repeat: { kind: 'Card', label: 'Повторить оплату', enabled: context.canPayNow, blockedReason: context.canPayNow ? null : context.payBlockedReason },
        alternative: { kind: 'Cash', label: 'Наличными', enabled: context.canPayNow },
        canClose: true,
      }
    case 'late-slip':
      return {
        ...plain,
        title: 'Найден слип после отказа',
        text: 'После отказа в кабинете AQSI найден слип на ту же сумму. Проверьте терминал и выберите, была ли оплата. Повторить оплату можно после выбора.',
        tone: 'danger',
        steps: steps.map((step) => (step.key === 'payment' ? { ...step, state: 'unknown' as const, text: 'найден слип' } : step)),
        latePayment: context.latePayment,
      }
    case 'not-started': {
      const notice =
        pending?.phase === 'not-arrived'
          ? { tone: 'warning' as const, title: 'Команда не дошла', text: base.command?.text ?? 'Служба так и не узнала команду оплаты.', hint: null, meta: null }
          : defaults.notice
      const fiscal = cycle?.error != null && FISCAL_REFUSALS.has(cycle.error.code)
      return {
        ...plain,
        title: 'Оплата не началась',
        text: fiscal ? 'Оплата не начиналась, деньги не списаны. Касса не примет этот чек — причина ниже.' : 'Оплата не началась — деньги не списаны, корзина цела.',
        tone: 'danger',
        steps: steps.map((step) =>
          step.key === 'payment' ? { ...step, state: 'failed' as const, text: 'не началась' } : { ...step, state: 'waiting' as const, text: 'после оплаты' },
        ),
        notice,
        repeat: {
          kind: attempt?.kind ?? context.kind,
          label: 'Повторить',
          enabled: context.canPayNow,
          blockedReason: context.canPayNow ? null : context.payBlockedReason,
        },
        canClose: true,
      }
    }
    case 'manual':
      return {
        ...plain,
        title: 'Ручная сверка',
        text:
          status?.kind === 'receipt'
            ? 'Отметьте, что видно на терминале и на кассе: списаны ли деньги и пробит ли чек в ФН. Повторять оплату нельзя.'
            : 'Проверьте терминал, кассу и кабинет и запишите результат. Повторять оплату до сверки нельзя.',
        tone: 'warning',
        canCollapse: false,
        collapsed: false,
        resolve: resolveFormOf(operation, context.resolveDraft ?? EMPTY_RESOLVE_DRAFT, status, sale, base.seat !== null, cycle ?? null),
      }
    default: {
      const starting = stage === 'preparing' && pending === undefined ? (attempt?.kind ?? context.kind) : null
      const preparingText = stage === 'preparing' && cycle?.phase === 'sending' ? cycle.note : null
      const progress = progressVm(base, sale, status, now, starting, preparingText, pending)
      if (progress === null) {
        return { ...plain, title: 'Уточняем оплату', text: 'Читаем продажу у службы…', tone: 'neutral', waiting: true }
      }
      const receiptUnknown = sale?.fiscal === 'Unknown'
      // Ручная сверка с формой — только у операции оплаты (слип); исход чека выясняет панель «Проверка в AQSI» (решение владельца 04.11.2026).
      const manual = progress.manualRequired && stage === 'checking'
      const recovery = stage === 'receipt' || stage === 'no-receipt' ? context.recovery : null
      const purchase = sale === null ? undefined : purchaseOf(sale, status)
      const adopted = attempt === null || attempt.id.startsWith('sale:')
      const waitingSince =
        stage === 'checking'
          ? pending?.phase === 'unknown'
            ? pending.startedAt
            : (purchase?.sentAt ?? (attempt === null || adopted ? null : attempt.startedAt))
          : stage === 'receipt' && receiptUnknown
            ? (status?.sentAt ?? null)
            : null
      const flow = paymentFlowOfProgress(progress, key, {
        ...defaults,
        waitingSince,
        // Деньги ещё не уходили (нет команды оплаты): «Отменить» просто закрывает попытку (RP-SALE-FAST, шаг 4).
        // Оплата ещё не подтверждена службой (команды нет, в пути или ждёт повтора): «Отменить» останавливает ожидание, чек и деньги не трогает.
        canCancelPreparation: stage === 'preparing' && (pending === undefined || (pending.kind === 'pay' && pending.phase === 'sending')),
        manualRequired: manual,
        // Чек неизвестен и нужна ручная сверка: «Повторять чек нельзя» — кнопки повтора нет (RP-FB35 N3); при панели — одно её действие.
        retry: (receiptUnknown && progress.manualRequired) || recovery !== null ? null : context.retry,
        park: context.park,
        canCancelPayment: stage !== 'preparing' && progress.canCancelPayment && context.cancelReady,
      })
      // Панель: окно не сворачивается («Свернуть» рядом с «Отложить продажу» не стоит никогда, К-126), «Пробить чек» — только её действием.
      return recovery === null
        ? { ...flow, stage }
        : { ...flow, stage, recovery, canFiscalize: false, canCollapse: false, collapsed: false }
    }
  }
}

// ——— RP-FB35 K7: причина зависания чека, предупреждения службы, повтор чека ———

/** Окно округления «сейчас» по умолчанию. */
export const CLOCK_BUCKET_MS = 15_000
/** Команда чека «висит» дольше этого — кассиру называют причину. */
export const RECEIPT_STUCK_AFTER_MS = 60_000
export { LATE_PAYMENT_SLIP, SERVICE_RECEIPT_GRACE_MS } from './paymentFlow'

const momentOf = (iso: string | null): number | null => {
  if (iso === null) return null
  const value = Date.parse(iso)
  return Number.isNaN(value) ? null : value
}

/** Целых минут ожидания: меньше минуты не называем. */
function waitedMinutes(since: number | null, now: number): number {
  return since === null || now <= since ? 0 : Math.floor((now - since) / 60_000)
}

function waitedSuffix(since: number | null, now: number): string {
  const minutes = waitedMinutes(since, now)
  return minutes >= 1 ? ` Ждём ${waitSpanText(minutes * 60_000)}.` : ''
}

/** Причина состояния чека словами: по статусу операции чека в кабинете и времени ожидания. */
export interface ReceiptCause {
  text: string
  tone: Tone
  /** Ждём дольше обычного: касса не берёт команду. */
  stuck: boolean
}

/**
 * Что происходит с чеком, который отправлен кассе (`Pending`/`Processing`/`Error` кабинета AQSI). Источник — данные службы
 * (операция чека и её статус), ничего не выдумывается; текст ошибки кабинета — дословно (ADR-8).
 */
export function receiptCauseOf(sale: AqsiSale, operation: AqsiSaleOperation | undefined, now: number): ReceiptCause {
  const current =
    operation !== undefined &&
    operation.kind === 'receipt' &&
    sale.receiptOperationId !== null &&
    sameId(operation.operationId, sale.receiptOperationId)
      ? operation
      : undefined
  const since = momentOf(current?.sentAt ?? current?.createdAt ?? sale.updatedAt)
  const waited = waitedSuffix(since, now)
  if (current === undefined) return { text: `Статус чека ещё не прочитан — уточняем результат.${waited}`, tone: 'warning', stuck: false }
  const status = current.providerStatus
  if (status === 'Error' || current.state === 'Failed') {
    return { text: receiptFailureText(sale, current) ?? (status === 'Error' ? 'Кабинет сообщил об ошибке чека' : 'Чек не выполнен'), tone: 'danger', stuck: false }
  }
  if (current.state === 'Unknown') return { text: `Исход чека неизвестен — проверяем на кассе.${waited}`, tone: 'warning', stuck: false }
  if (status === 'Processing' || status === 'Finishing') return { text: `Касса печатает чек.${waited}`, tone: 'neutral', stuck: false }
  const long = since !== null && now - since >= RECEIPT_STUCK_AFTER_MS
  if (current.state === 'Pending') {
    return {
      text: long ? `Команда чека не ушла в кабинет.${waited}` : 'Команда чека записана и отправляется в кабинет…',
      tone: long ? 'warning' : 'neutral',
      stuck: long,
    }
  }
  // Кабинет сам снял команду (срок или отмена), а чека нет: касса её не исполнит — «не берёт команду» тут было бы неправдой.
  if (status === 'Timeout') {
    return { text: 'Истёк срок ожидания ответа кабинета. Исход чека ещё не подтверждён: служба сверяет команду и журнал.', tone: 'warning', stuck: true }
  }
  if (status === 'Canceled') {
    return { text: 'Кабинет сообщил об отмене команды чека. Служба сверяет результат и журнал.', tone: 'warning', stuck: true }
  }
  if (status === 'Completed') {
    return { text: 'Кабинет сообщил о выполнении команды. Служба проверяет фискальные реквизиты и обновляет продажу.', tone: 'warning', stuck: true }
  }
  // Отправлена, кабинет ответил `Pending`: касса ещё не взяла команду.
  return long
    ? { text: `Касса не берёт команду: выключена или нет связи с кабинетом.${waited}`, tone: 'warning', stuck: true }
    : { text: 'Команда у кабинета — ждём, пока касса её возьмёт…', tone: 'neutral', stuck: false }
}

/** Текст отказа чека — кабинета, дословно (ADR-8); службы нет — `null`. */
function receiptFailureText(sale: AqsiSale, operation: AqsiSaleOperation | undefined): string | null {
  const text = operation?.providerMessage ?? operation?.note ?? sale.lastMessage
  return text === null || text === undefined || text.trim() === '' ? null : text
}

const ATTENTION_TEXT: Readonly<Record<string, string>> = {
  'duplicate-suspected': 'Пробито два чека — оформите чек возврата на лишний. Сверьте смену',
  'payment-too-old': 'Оплата старше допустимого — чек автоматически не отправлен. Пробейте чек',
  'payment-slips-unreadable': 'Не удалось прочитать список слипов кабинета — оплату сверяют вручную',
  'late-payment-slip':
    'После отмены оплаты в кабинете AQSI нашёлся слип покупки на эту сумму с этой кассы — покупатель, возможно, всё-таки оплатил. Проверьте терминал и выберите: «Оплаты не было» или «Оплата прошла — пробить чек»',
}

/** Уровень называется словом: он виден и без цвета. */
const ATTENTION_LEVEL_WORD: Readonly<Record<AqsiSaleAttention['level'], string>> = { critical: 'Важно', warning: 'Внимание' }

/** Строки предупреждений: «Важно: …» / «Внимание: …». Текст службы — дословно, свой — только для известного кода. */
export function attentionLines(attention: readonly AqsiSaleAttention[]): string[] {
  return attention.map((item) => {
    const text = item.text ?? ATTENTION_TEXT[item.code] ?? item.code
    const next = formatClock(item.nextAttemptAt)
    return `${ATTENTION_LEVEL_WORD[item.level]}: ${text.replace(/[.\s]+$/, '')}.${next !== null ? ` Служба повторит в ${next}.` : ''}`
  })
}

const worstTone = (attention: readonly AqsiSaleAttention[]): Tone | null =>
  attention.some((item) => item.level === 'critical') ? 'danger' : attention.length > 0 ? 'warning' : null

export const RECEIPT_RETRY_CHECKED_LABEL: Readonly<Record<ReceiptRetryChecked, string>> = {
  register: 'Смотрел кассу — чека нет',
  cabinet: 'Смотрел кабинет AQSI — чека нет',
  other: 'Другое',
}

export const isReceiptRetryChecked = (value: string): value is ReceiptRetryChecked => value in RECEIPT_RETRY_CHECKED_LABEL

/** Что уходит в поле `checked`: готовая фраза или «Другое: <текст человека>». */
export function receiptRetryCheckedText(draft: { checked: string | null; checkedText: string }): string {
  if (draft.checked === null || !isReceiptRetryChecked(draft.checked)) return ''
  if (draft.checked !== 'other') return RECEIPT_RETRY_CHECKED_LABEL[draft.checked]
  return draft.checkedText.trim() === '' ? '' : `${RECEIPT_RETRY_CHECKED_LABEL.other}: ${draft.checkedText.trim()}`
}

/** Подсказки к отказам «Отложить продажу» — сверх дословного текста службы в баннере. */
const PARK_HINTS: Readonly<Record<string, string>> = {
  'park-not-allowed': 'Отложить можно только продажу, по которой деньги взяты, а чек ещё не пробит',
  'sale-parked': 'Продажа уже отложена — она в «Чеки» → «Отложенные продажи»',
  'receipt-sending': 'Дождитесь результата отправки чека и повторите откладывание',
  'parking-disabled': 'Откладывание продаж на этой установке не включено',
  'version-mismatch': 'Продажа изменилась — окно перечитало её. Проверьте и повторите',
}

const PARK_CONFIRM =
  'Можно начать следующую продажу. Оплата уже получена и повторно списываться не будет. ' +
  'Чек сохранится на сервере и будет виден справа в «Чеки в фоне», в том числе после обновления страницы. ' +
  'Служба проверит результат команды и при необходимости повторит чек по действующим правилам, когда касса свободна. ' +
  'Если чек не пробьётся, вернитесь к нему через «Открыть список».'

const PARK_LABEL = 'Отложить продажу'

/**
 * Панель «Проверка в AQSI» показывает «Отложить продажу» всегда; продажу, которую служба откладывать не даёт (наличные, уже идёт
 * команда), — недоступной кнопкой со словами почему, а не пустым местом.
 */
const PARK_UNAVAILABLE: ParkVm = {
  label: PARK_LABEL,
  canOpen: false,
  blockedText: 'Эту продажу отложить нельзя: откладывается продажа, оплата которой подтверждена терминалом',
  form: null,
}

/** Служба в «Проверке в AQSI» сказала, что отложить сейчас нельзя (`secondary.parkAllowed = false`). */
const PARK_REFUSED: ParkVm = { ...PARK_UNAVAILABLE, blockedText: 'Служба сейчас не разрешает отложить эту продажу — проверьте состояние ещё раз' }

/** «Отложить продажу» в панели «Проверка в AQSI»: всегда видна; недоступна — со словами почему. */
function panelParkOf(park: ParkVm | null, state: AqsiSaleState | undefined): ParkVm {
  if (state?.parkAllowed === false) return park === null ? PARK_REFUSED : { ...park, canOpen: false, blockedText: PARK_REFUSED.blockedText }
  return park ?? PARK_UNAVAILABLE
}

/**
 * «Отложить продажу»: деньги взяты, а чек не пробит, включая отказ до первой отправки. Подтверждение с
 * необязательной причиной. Автоотмены и автовозврата денег нет.
 */
function parkVmOf(
  sale: AqsiSale | null,
  seat: SaleSeatIntent,
  ready: boolean,
  blocked: boolean,
  feedback: CommandFeedback | null,
): ParkVm | null {
  if (sale === null || sale.isFinished || sale.isParked) return null
  const moneyIn = sale.payment === 'Succeeded'
  if (!moneyIn || !['Pending', 'InProgress', 'Unknown', 'Failed'].includes(sale.fiscal)) return null
  const draft = seat.park !== null && sameId(seat.park.saleId, sale.id) ? seat.park : null
  const sending = feedback?.command === 'sale.park' && feedback.phase === 'sending'
  const code = feedback?.command === 'sale.park' && feedback.phase === 'rejected' ? (feedback.error?.code ?? null) : null
  const blockedText = blocked ? 'Исход прошлой команды неизвестен — сначала проверьте статус' : null
  return {
    // Всегда «Отложить продажу» (решение владельца 04.11.2026, К-126): «Свернуть и продолжить» владелец не нашёл как откладывание.
    label: PARK_LABEL,
    canOpen: ready && draft === null && !sending,
    blockedText,
    form:
      draft === null
        ? null
        : {
            confirmText: PARK_CONFIRM,
            reason: draft.reason,
            canSubmit: ready && !sending,
            submitBlockedReason: sending ? 'Продажа откладывается…' : blockedText,
            sending,
            hint: code !== null ? (PARK_HINTS[code] ?? null) : null,
          },
  }
}


/**
 * Запоздавший слип после отмены оплаты: покупатель, возможно, всё-таки заплатил. Текст - службы, дословно; решение принимает человек:
 * «Оплаты не было» или «Оплата прошла — пробить чек». Блока нет, пока у продажи нет такого внимания или она закрыта.
 */
function latePaymentVmOf(sale: AqsiSale | null, ready: boolean, blocked: boolean, feedback: CommandFeedback | null): LatePaymentVm | null {
  if (sale === null || sale.isFinished) return null
  const item = sale.attention.find((entry) => entry.code === LATE_PAYMENT_SLIP)
  if (item === undefined) return null
  const sending = feedback?.command === 'sale.late-payment' && feedback.phase === 'sending'
  const blockedText = blocked ? 'Исход прошлой команды неизвестен — сначала проверьте статус' : null
  return {
    // «Важно: …» - уровень словом, не только цветом.
    text: attentionLines([item]).join(' '),
    canAct: ready && !sending && blockedText === null,
    sending,
    blockedText,
  }
}

/** «Предыдущая продажа отложена…»: пока у кассы есть отложенные продажи с деньгами; ссылка ведёт в их список. */
function parkedNoticeOf(count: number | undefined): ParkedNoticeVm | null {
  if (count === undefined || count <= 0) return null
  return {
    text:
      count === 1
        ? 'Предыдущая продажа сохранена в фоне: проверьте результат оплаты и чека.'
        : `Продаж в фоне: ${count} — проверьте результаты оплаты и чеков.`,
    linkLabel: 'Открыть список',
  }
}

export function receiptRetryBlockedReason(draft: ReceiptRetryDraft): string | null {
  if (draft.checked === null) return 'Выберите, что вы проверили'
  if (draft.checked === 'other' && draft.checkedText.trim() === '') return 'Опишите, что вы проверили'
  if (draft.reason.trim() === '') return 'Укажите причину'
  return null
}

/**
 * «Повторить проведение» (RP-FB35 K4): одна кнопка без формы. Есть, пока деньги взяты, а чек не пробит или завис; служба сама снимает старую команду,
 * сверяет журнал и пробивает заново. Закрыта при подозрении на дубль и у завершённой продажи; пока касса печатает - недоступна со словами.
 */
function retryVmOf(
  sale: AqsiSale | null,
  _seat: SaleSeatIntent,
  ready: boolean,
  blocked: boolean,
  feedback: CommandFeedback | null,
  _now: number,
): ReceiptRetryVm | null {
  if (sale === null || sale.isFinished) return null
  if (sale.attention.some((item) => item.code === 'duplicate-suspected')) return null
  // Наличные: служба повтор не принимает (409 payment-required) — основная кнопка там «Пробить чек» (RP-FB35 N3).
  if (sale.paymentKind !== 'Card' || sale.payment !== 'Succeeded') return null
  if (!['Pending', 'InProgress', 'Unknown', 'Failed'].includes(sale.fiscal)) return null
  const sending = feedback?.command === 'sale.retry' && feedback.phase === 'sending'
  const printing = sale.receiptCanRetryNow === false && sale.fiscal === 'InProgress'
  const blockedText = blocked
    ? 'Исход прошлой команды неизвестен — сначала проверьте статус'
    : sending
      ? 'Повтор выполняется…'
      : printing
        ? 'Касса печатает чек — подождите'
        : null
  return {
    label: 'Повторить проведение',
    canOpen: ready && !sending && !printing,
    blockedText,
    form: null,
  }
}

/**
 * Две отметки проведения (RP-FB35 K4): «① Оплата (терминал)» (у наличных - «Наличные получены») и «② Чек (фискальный накопитель)». Готовый шаг -
 * галочка, иначе слово состояния; ничего не выдумывается - всё из состояния продажи и команды в пути.
 */
export function stepsOf(sale: AqsiSale | null, command: SaleBaseVm['command'], now: number, operation?: AqsiSaleOperation): readonly ProgressStepVm[] {
  const cash = sale?.paymentKind === 'Cash'
  const label = cash ? 'Наличные получены' : '① Оплата (терминал)'
  let payment: ProgressStepVm
  if (sale === null || command?.phase === 'sending' && command.kindText === 'Оплата') {
    payment = { key: 'payment', label, state: 'active', text: cash ? 'отправляем' : 'отправляем на терминал' }
  } else if (sale.payment === 'Succeeded' || sale.payment === 'NotRequired') {
    payment = { key: 'payment', label, state: 'done', text: cash ? 'получены' : 'оплачено' }
  } else if (sale.payment === 'InProgress') {
    payment = { key: 'payment', label, state: 'active', text: 'ждём карту' }
  } else if (sale.payment === 'Unknown') {
    payment = { key: 'payment', label, state: 'unknown', text: 'неизвестно' }
  } else if (sale.payment === 'Failed') {
    payment = { key: 'payment', label, state: 'failed', text: 'не прошла' }
  } else {
    payment = { key: 'payment', label, state: 'waiting', text: cash ? 'не получены' : 'ждёт оплаты' }
  }

  const name = '② Чек (фискальный накопитель)'
  const moneyIn = payment.state === 'done'
  let receipt: ProgressStepVm
  if (sale === null || !moneyIn) receipt = { key: 'receipt', label: name, state: 'waiting', text: 'после оплаты' }
  else if (sale.fiscal === 'Succeeded' || sale.fiscal === 'IssuedOutside') receipt = { key: 'receipt', label: name, state: 'done', text: 'пробит' }
  else if (sale.fiscal === 'InProgress') {
    const current = operation?.kind === 'receipt' && sale.receiptOperationId !== null && sameId(operation.operationId, sale.receiptOperationId) ? operation : undefined
    if (current?.state === 'Succeeded') receipt = { key: 'receipt', label: name, state: 'done', text: 'пробит' }
    else if (current?.providerStatus === 'Error') receipt = { key: 'receipt', label: name, state: 'failed', text: 'ошибка чека' }
    else if (current?.state === 'Failed' || current?.providerStatus === 'Canceled') receipt = { key: 'receipt', label: name, state: 'failed', text: 'не пробит' }
    else if (current?.providerStatus === 'Processing' || current?.providerStatus === 'Finishing') receipt = { key: 'receipt', label: name, state: 'active', text: 'печатаем' }
    else if (current?.providerStatus === 'Pending') receipt = { key: 'receipt', label: name, state: 'active', text: 'ждёт кассу' }
    else if (current?.state === 'Pending') receipt = { key: 'receipt', label: name, state: 'active', text: 'отправляем' }
    else receipt = { key: 'receipt', label: name, state: 'unknown', text: 'уточняем чек' }
  }
  else if (sale.fiscal === 'Unknown') receipt = { key: 'receipt', label: name, state: 'unknown', text: 'неизвестно' }
  else if (sale.fiscal === 'Failed') receipt = { key: 'receipt', label: name, state: 'failed', text: 'не пробит' }
  else {
    const paidAt = momentOf(sale.updatedAt)
    const fresh = paidAt !== null && now - paidAt < SERVICE_RECEIPT_GRACE_MS && sale.attention.length === 0
    receipt = fresh ? { key: 'receipt', label: name, state: 'active', text: 'отправляем' } : { key: 'receipt', label: name, state: 'failed', text: 'не пробит' }
  }

  return [payment, receipt]
}

const MONEY_DONE_TEXT: Readonly<Record<PaymentKind, string>> = { Card: 'получено картой', Cash: 'получено наличными' }

/**
 * Ход оплаты для окна поверх РМК. Кассир видит только то, что требует его действия или внимания сейчас: у терминала — сумма и «Приложите
 * карту к терминалу» с кнопкой «Отменить»; дальше всё идёт само (оплата, чек, уточнение неясного исхода опросом). Человека зовут
 * событием: «деньги получены, чек не пробит» и «нужна ручная сверка». Неизвестный исход не повторяется, а сверяется. Номер операции и
 * подробности службы — только в «Подробнее» проблемного этапа.
 */
function progressVm(
  base: SaleBaseVm,
  sale: AqsiSale | null,
  status: AqsiSaleOperation | undefined,
  now: number,
  /** Нажата оплата этим способом, команда ещё не ушла: проверяем связь с кассой. Окно открывается сразу, а не после проверки. */
  starting: PaymentKind | null = null,
  /** Что идёт в подготовке («Проверяем связь с кассой…», «Меняем способ оплаты…»); `null` — проверка связи. */
  preparingText: string | null = null,
  /** Команда оплаты или чека в пути: номер попытки и пауза перед повтором в словах окна. */
  pending: SaleCommand | undefined = undefined,
): ProgressVm | null {
  const operation = base.operation
  const amountText = sale !== null ? formatKopecks(sale.totalKopecks) : base.totalText
  const details = [
    ...(operation?.details ?? []),
    ...(operation !== null ? [`${operation.kindText}: ${operation.stateText}`, `Номер операции: ${operation.operationId}`] : []),
  ]
  const manual = operation?.manualRequired === true
  const common = {
    steps: stepsOf(sale, base.command, now, status),
    amountText,
    details,
    manualRequired: manual,
    canFiscalize: false,
    canMarkOutside: false,
    canCancelPayment: false,
  }
  const command = base.command
  // Новое нажатие после «команда не дошла» — тоже подготовка: прошлая команда уже ничего не решает.
  const freshAttempt = starting !== null && (command === null || command.phase === 'not-arrived')
  if (command?.phase === 'sending' || freshAttempt) {
    const paying = freshAttempt || command === null || command.kindText === 'Оплата'
    const card = (freshAttempt ? starting : sale?.paymentKind) !== 'Cash'
    // Новая попытка ещё не ушла, а на продаже — итог прошлой («не прошла»): этапы новой попытки, а не прошлой.
    const steps =
      freshAttempt
        ? common.steps.map((step) =>
            step.key === 'payment' ? { ...step, state: 'active' as const, text: 'проверяем связь' } : { ...step, state: 'waiting' as const, text: 'после оплаты' },
          )
        : common.steps
    return {
      ...common,
      steps,
      phase: 'sending',
      title: paying ? (card ? 'Оплата картой' : 'Оплата наличными') : (command?.kindText ?? ''),
      text: freshAttempt
        ? (preparingText ?? 'Проверяем связь с кассой…')
        : pending !== undefined
          ? commandSendingText(pending, paying && card ? 'Отправляем на терминал…' : 'Отправляем кассе…')
          : paying && card
            ? 'Отправляем на терминал…'
            : 'Отправляем кассе…',
      // Ждём паузу перед повтором: связи нет — тон предупреждения, а не спокойное «отправляем».
      tone: pending?.retryAt != null ? 'warning' : 'neutral',
      waiting: true,
    }
  }
  if (command?.phase === 'unknown') {
    return {
      ...common,
      phase: 'command-unknown',
      title: 'Уточняем результат',
      text: 'Ответ не получен — проверяем на кассе. Повторять ничего не нужно.',
      tone: 'warning',
      waiting: true,
    }
  }
  if (sale === null || sale.isFinished) return null
  if (sale.payment === 'InProgress') {
    if (status?.kind === 'purchase' && sale.purchaseOperationId !== null && sameId(status.operationId, sale.purchaseOperationId) &&
      (status.providerStatus === 'Error' || status.providerStatus === 'Canceled' || status.providerStatus === 'Timeout')) {
      // Терминал ответил, а продажа ещё идёт: служба с `currentOperation` (0.89.0) решает отказ той же версией продажи, и этот этап не
      // виден; у прежней службы — миг между чтениями. Без «Фиксируем отказ» и без обещания секунд: окно уточняет и считает время.
      return {
        ...common,
        phase: 'money-unknown',
        title: 'Проверяем оплату',
        text: [
          status.providerStatus === 'Canceled'
            ? 'Терминал сообщил об отмене оплаты.'
            : status.providerStatus === 'Timeout'
              ? 'Терминал не вернул итог оплаты.'
              : 'Терминал сообщил об отказе.',
          status.providerMessage !== null && status.providerMessage !== '' ? `Ответ кассы: ${status.providerMessage}` : null,
          'Проверяем в кабинете AQSI, не списаны ли деньги, — так мы не спишем их дважды.',
        ].filter((part) => part !== null).join('\n'),
        steps: common.steps.map((step) => step.key === 'payment' ? { ...step, state: 'unknown' as const, text: 'проверяем списание' } : step),
        tone: 'warning',
        waiting: true,
      }
    }
    // Срок команды прошёл, а итог не доказан (слипы не прочитаны или слип есть): служба просит человека. «Приложите карту» здесь неправда —
    // окно показывает сверку (23.10.2026: оплата висела ночь с «Приложите карту» и без выхода).
    if (manual) {
      return {
        ...common,
        phase: 'money-unknown',
        title: 'Нужна ручная сверка',
        text: 'Терминал не вернул итог оплаты за срок команды, а служба не смогла доказать его по слипам кабинета AQSI.\n' +
          'Проверьте терминал и кабинет и запишите результат («Ручная сверка»). Повторять оплату до сверки нельзя.',
        tone: 'warning',
        waiting: false,
        canCancelPayment: sale.paymentKind === 'Card' && operation?.accepted === true,
      }
    }
    return {
      ...common,
      phase: 'terminal',
      title: 'Оплата картой',
      text: status?.kind === 'purchase' && sale.purchaseOperationId !== null && sameId(status.operationId, sale.purchaseOperationId) && status.providerStatus === 'Pending'
        ? 'Команда у кабинета — ждём, пока касса её возьмёт…'
        : 'Приложите карту к терминалу',
      tone: 'neutral',
      waiting: true,
      canCancelPayment: sale.paymentKind === 'Card' && operation?.accepted === true,
    }
  }
  if (sale.payment === 'Unknown') {
    return {
      ...common,
      phase: 'money-unknown',
      title: manual ? 'Нужна ручная сверка' : 'Уточняем оплату',
      text: manual
        ? 'Не удалось выяснить, прошла ли оплата. «Отменить» сначала проверит кассу и кабинет: если оплата уже прошла, программа это скажет. ' +
          'Иначе проверьте терминал и кабинет и запишите результат. Повторять оплату нельзя.'
        : 'Проверяем на кассе, прошла ли оплата. Программа делает это сама, даже если связь пропадала. «Отменить» сначала проверит, не прошла ли оплата.',
      tone: 'warning',
      waiting: !manual,
      canCancelPayment: sale.paymentKind === 'Card' && operation?.accepted === true,
    }
  }
  const warnings = attentionLines(sale.attention)
  const attentionTone = worstTone(sale.attention)
  // Предупреждения службы идут первыми: «Важно: …» / «Внимание: …» — уровень назван словом, не только цветом.
  const withWarnings = (text: string) => [...warnings, text].join(' ')
  if (sale.fiscal === 'InProgress') {
    // Чек у кассы: причину называет статус операции чека в кабинете и время ожидания, а не вечное «Печатаем чек».
    const cause = receiptCauseOf(sale, status, now)
    const receiptStep = common.steps[1]
    const shiftExpired = sale.attention.some((item) => item.code === 'shift-expired')
    const recovery = shiftExpired
      ? 'Нажмите «Отложить чек» и подтвердите. В «Действия» → «Смена» закройте текущую смену и откройте новую. Затем верните продажу из отложенных и нажмите «Повторить проведение»: служба сначала сверит прежнюю команду.'
      : ''
    return {
      ...common,
      phase: 'receipt',
      title: receiptStep?.state === 'done' ? 'Чек пробит' : receiptStep?.state === 'failed' ? 'Ошибка чека' : receiptStep?.state === 'unknown' ? 'Уточняем чек' : receiptStep?.text === 'печатаем' ? 'Печатаем чек' : 'Ожидаем кассу',
      text: withWarnings([cause.text, recovery].filter(Boolean).join(' ')),
      watchNote: receiptWatchNote(status, now),
      tone: attentionTone ?? cause.tone,
      waiting: receiptStep?.state === 'active',
    }
  }
  if (sale.fiscal === 'Unknown') {
    const since = momentOf(status?.sentAt ?? status?.createdAt ?? sale.updatedAt)
    const waited = waitedSuffix(since, now)
    return {
      ...common,
      phase: 'receipt-unknown',
      // Срок команды прошёл, а исход не доказан: что нашли в AQSI и что делать — панель «Проверка в AQSI» (RP-CASH-RECOVERY §4.1).
      title: manual ? 'Чек не подтверждён' : 'Уточняем чек',
      text: withWarnings(
        manual
          ? 'Деньги получены, а пробит ли чек — не подтверждено. Ниже — что нашли в AQSI и что сделать.'
          : `Проверяем на кассе, пробит ли чек. Повторять ничего не нужно.${waited}`,
      ),
      tone: attentionTone ?? 'warning',
      waiting: !manual,
    }
  }
  const moneyIn = sale.paymentKind === 'Card' ? sale.payment === 'Succeeded' : sale.payment === 'NotRequired'
  // Карта: чек отправляет СЛУЖБА (вебхук оплаты, затем сторож). Окно ничего не отправляет само: в первые секунды ждёт службу,
  // дальше говорит правду — «чек не отправлен» — и оставляет кнопку человеку.
  const paidAt = momentOf(sale.updatedAt)
  const fresh = paidAt !== null && now - paidAt < SERVICE_RECEIPT_GRACE_MS
  if (moneyIn && sale.fiscal === 'Pending' && sale.paymentKind === 'Card' && fresh && sale.attention.length === 0) {
    return { ...common, phase: 'receipt', title: 'Оплата прошла', text: 'Служба отправляет чек на кассу…', tone: 'neutral', waiting: true }
  }
  // Деньги получены, а чека нет: картой — после оплаты; наличными — касса отказала в чеке (доказанно).
  if (moneyIn && (sale.fiscal === 'Failed' || (sale.paymentKind === 'Card' && sale.fiscal === 'Pending'))) {
    const money = `${amountText} ${MONEY_DONE_TEXT[sale.paymentKind]}.`
    const waited = waitedSuffix(paidAt, now)
    // Failed: текст отказа кабинета — дословно; Pending: служба чек так и не отправила (причина — в предупреждении, если оно есть).
    const failure = sale.fiscal === 'Failed' ? receiptFailureText(sale, status) : null
    const reason =
      sale.fiscal === 'Failed'
        ? failure === null
          ? ''
          : `Чек не пробит: ${failure}`
        : warnings.length > 0
          ? ''
          : `Служба ещё не отправила чек.${waited}`
    return {
      ...common,
      phase: 'no-receipt',
      title: 'Деньги получены, чек не пробит',
      text: [...warnings, money, reason, 'Пробейте чек.'].filter((part) => part !== '').join(' '),
      tone: attentionTone ?? 'warning',
      waiting: false,
      canFiscalize: base.canFiscalize,
      canMarkOutside: base.canMarkIssuedOutside || base.outside !== null,
    }
  }
  return null
}

// ——— ручная сверка ———

export interface ResolveVmInput {
  resourceId: string
  operationId: string
  operation: QueryResultLike<AqsiSaleOperation>
  /** Продажа операции — для сверки чека по галочкам (К-128); не передана — галочки ждут её чтения. */
  sale?: QueryResultLike<AqsiSale>
  draft: ResolveDraft
  feedback: CommandFeedback | undefined
}

export function resolveBlockedReason(draft: ResolveDraft, resultIdRequired: boolean): string | null {
  if (draft.phase === 'sending') return 'Сверка отправляется…'
  if (draft.phase === 'unknown') return 'Ответ на сверку не получен — сначала проверьте статус'
  if (draft.phase === 'done') return 'Сверка записана'
  if (draft.outcome === null) return 'Выберите итог: исполнена операция или нет'
  if (draft.reason.trim() === '') return 'Укажите причину'
  if (draft.checked.trim() === '') return 'Укажите, что проверено на кассе или в кабинете'
  if (resultIdRequired && draft.outcome === 'Succeeded' && draft.resultId.trim() === '') {
    return 'Для исполненного списания нужен номер слипа: без него чек не пробить'
  }
  return null
}

export const makeSelectResolveVm = () => {
  const selectView = makeQueryViewSelector(
    (input: ResolveVmInput) => input.operation,
    () => false,
  )
  return createSelector(
    [
      selectView,
      (input: ResolveVmInput) => input.operation.data,
      (input: ResolveVmInput) => input.resourceId,
      (input: ResolveVmInput) => input.operationId,
      (input: ResolveVmInput) => input.draft,
      (input: ResolveVmInput) => input.feedback ?? null,
      (input: ResolveVmInput) => input.sale?.data,
    ],
    (query, data, resourceId, operationId, draft, feedback, saleData): ResolveVm => {
      const status = readyData(query, data)
      const resultIdRequired = status !== undefined && isSlipOperation(status.kind)
      const allowed = status !== undefined && status.nextAction === 'ManualReconciliation'
      const sale = saleData !== undefined && status !== undefined && sameId(saleData.id, status.saleId) ? saleData : null
      const verdict = receiptVerdictOf({ sale, status, draft, allowed, seatKnown: status?.deviceId != null, numberRequired: receiptNumberRequiredBy(feedback) })
      const blocked = verdict === null ? resolveBlockedReason(draft, resultIdRequired) : verdict.blockedReason
      return {
        resourceId,
        operationId,
        query,
        kindText: status !== undefined ? KIND_TEXT[status.kind] : 'Операция',
        stateText: status !== undefined ? OPERATION_STATE[status.state].text : '',
        details: status !== undefined ? operationDetails(status) : [],
        allowed,
        allowedReason:
          status === undefined
            ? null
            : allowed
              ? null
              : status.resolution !== null || status.nextAction === 'None'
                ? 'Итог операции уже известен — сверять нечего'
                : 'Касса ещё может исполнить операцию: ручная сверка доступна после срока команды. Пока окно само проверяет операцию на кассе.',
        resolution: status?.resolution ?? null,
        draft,
        resultIdRequired,
        canSubmit: verdict === null && allowed && blocked === null,
        submitBlockedReason: allowed && verdict === null ? blocked : null,
        feedback,
        verdict,
      }
    },
  )
}
